import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  StyleSheet,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Platform,
  Modal,
  useWindowDimensions,
} from "react-native";
import { WebView } from "react-native-webview";
import { Ionicons } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import { ContactModel, GroupModel, MediaModel, MemoryModel } from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedButton } from "../ThemedButton";
import { ThemedToolTip } from "../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedChip } from "../ThemedChip";
import { ThemedTableEditor } from "./ThemedTableEditor";
import { ThemedMediaEmbed } from "./ThemedMediaEmbed";
import {
  EditToolbar,
  MediaToolbar,
  WebLinkToolbar,
  TableToolbar,
  InsertToolbar,
  AiToolbar,
  CaptureModeToolbar,
  type CaptureMode,
} from "./toolbars";
import { ContactSelector } from "@/components/contacts/ContactSelector";
import { GroupSelector } from "@/components/groups/GroupSelector";
import { MediaSelector } from "@/components/media/MediaSelector";
import { MemorySelector } from "@/components/memory/MemorySelector";
import {
  IThemedEditorProps,
  MentionEntityType,
  IMentionItem,
  IEditorBlock,
  EditorBlockType,
  IEditorFormatState,
  IEditorChipToken,
  MediaSize,
  IParsedHtmlNode,
} from "./types";
import {
  parseHtmlToBlocks,
  serializeBlocksToHtml,
  parseHtmlNodes,
  escapeHtml,
  stripHtmlTags,
  sanitizeFormattingHtml,
  mapPlainOffsetToHtmlOffset,
  mapHtmlOffsetToPlainOffset,
  restoreDomSelection,
  extractEditorPlainTextAndOffset,
} from "./html-sanitizer";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useAuth } from "@/components/auth/useAuth";
import { getCollabWebSocketUrl, getCollaboratorColor, type ICollaboratorState } from "./collab-utils";

/**
 * ThemedEditor: Intuitive Normal-Text Rich Description Editor & Viewer.
 *
 * Core Features:
 * - Stores clean, sanitized HTML while editing is completely in normal text (zero raw HTML tags).
 * - Toolbar button selection preservation on web: onMouseDown: (e: any) => e.preventDefault()
 * - Single continuous text block: Text flows freely across paragraphs and around embedded media.
 * - Standard Platform Selectors: Direct integration with ContactSelector, GroupSelector, MediaSelector, and MemorySelector.
 * - In-place direct formatted presentation: No separate preview tab mode needed.
 * - Zero-keyboard formatting: One-tap toolbar buttons apply and remove formats.
 * - Active Formats Bar: Visibly displays active formats as badges with a direct [✕] remove button.
 * - Copy/Paste Media: Pasting media automatically matches existing Media or uploads and links to Memory.
 * - In-place Media Sizing: Interactive 25%, 50%, 75%, 100% sizing overlays on embedded media.
 * - Interactive Tables: Popup buttons around tables (+Col on right, +Row on bottom) with kebab options.
 * - Tooltips on all toolbar buttons with responsive hover feedback.
 */
export const ThemedEditor: React.FC<IThemedEditorProps> = ({
  value,
  initialContent,
  entityId,
  entityType = "memory",
  collaborative,
  onFocus,
  onBlur,
  testID,
  onChange,
  onSelectionChange,
  mode = "edit",
  editable = true,
  placeholder = "Write description in normal text... Tap formats or use @ + # to mention",
  contacts = [],
  groups = [],
  media = [],
  memories = [],
  activeMemoryId,
  onMediaUpload,
  onChipPress,
  isItemFound,
  style,
  showRawHtml = false,
  accessibilityLabel,
  dynamicHeight = false,
  minLines = 4,
  maxScreenRatio = 0.75,
}) => {
  const { height: windowHeight } = useWindowDimensions();
  const NORMAL_LINE_HEIGHT = 22;
  const NORMAL_VERTICAL_PADDING = 28;
  const floorHeight = (minLines ?? 4) * NORMAL_LINE_HEIGHT; // e.g. 4 * 22 = 88px
  const computedMinHeight = dynamicHeight ? floorHeight + NORMAL_VERTICAL_PADDING : 280;
  const computedMaxHeight = dynamicHeight
    ? Math.max(computedMinHeight, Math.floor((windowHeight || 800) * (maxScreenRatio ?? 0.75)))
    : undefined;

  const resolvedContent = value ?? initialContent ?? "";
  const resolvedEntityId = entityId;
  const resolvedEntityType = entityType || "general";
  const isCollabActive = (collaborative ?? Boolean(resolvedEntityId)) && (Platform.OS === "web" || typeof WebSocket !== "undefined");

  const [activeTab, setActiveTab] = useState<"edit" | "view" | "raw">(mode === "view" ? "view" : "edit");
  const [blocks, setBlocks] = useState<IEditorBlock[]>(() => parseHtmlToBlocks(resolvedContent));
  // Latest blocks ref to avoid stale closures during debounced flow node updates
  const latestBlocksRef = useRef<IEditorBlock[]>(blocks);
  latestBlocksRef.current = blocks;

  // Active typing ref to prevent DOM selection thrashing and layout reflow during continuous keystrokes
  const isTypingRef = useRef<boolean>(false);
  const flowNodesDebounceTimerRef = useRef<any>(null);

  // Debounced flow nodes state: preserves DOM stability and eliminates chip redraw jitter while actively typing
  const [flowNodes, setFlowNodes] = useState<IParsedHtmlNode[]>(() => {
    const currentHtml = serializeBlocksToHtml(blocks);
    return parseHtmlNodes(currentHtml);
  });

  // Flush flow nodes immediately when structural document boundaries or interactive actions occur
  const flushFlowNodes = useCallback((targetBlocks?: IEditorBlock[]) => {
    if (flowNodesDebounceTimerRef.current) {
      clearTimeout(flowNodesDebounceTimerRef.current);
      flowNodesDebounceTimerRef.current = null;
    }
    const b = targetBlocks || latestBlocksRef.current;
    const currentHtml = serializeBlocksToHtml(b);
    setFlowNodes(parseHtmlNodes(currentHtml));
    isTypingRef.current = false;
  }, []);

  // Debounce redrawing flow nodes during continuous character typing (160ms window)
  const scheduleDebouncedFlowNodes = useCallback((targetBlocks: IEditorBlock[]) => {
    isTypingRef.current = true;
    if (flowNodesDebounceTimerRef.current) {
      clearTimeout(flowNodesDebounceTimerRef.current);
    }
    flowNodesDebounceTimerRef.current = setTimeout(() => {
      flowNodesDebounceTimerRef.current = null;
      const currentHtml = serializeBlocksToHtml(latestBlocksRef.current);
      setFlowNodes(parseHtmlNodes(currentHtml));
      isTypingRef.current = false;
    }, 160);
  }, []);

  useEffect(() => {
    return () => {
      if (flowNodesDebounceTimerRef.current) {
        clearTimeout(flowNodesDebounceTimerRef.current);
      }
    };
  }, []);
  const [focusedBlockIndex, setFocusedBlockIndex] = useState<number>(0);

  // Selection & Pending-Typing Armed Format State
  const [selection, setSelection] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const [typingFormats, setTypingFormats] = useState<IEditorFormatState>({});

  // Undo / Redo History Stack State
  const undoStackRef = useRef<IEditorBlock[][]>([]);
  const redoStackRef = useRef<IEditorBlock[][]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Format Painter State
  const [formatPainterArmed, setFormatPainterArmed] = useState(false);
  const [copiedFormats, setCopiedFormats] = useState<IEditorFormatState | null>(null);

  // Contextual Floating Popup Toolbar State
  const [activeToolbar, setActiveToolbar] = useState<"none" | "text" | "media" | "link" | "table">(
    mode === "view" ? "none" : "text"
  );
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({ top: 8, left: 12 });
  const [selectedMediaBlockId, setSelectedMediaBlockId] = useState<string | null>(null);
  const [selectedTableBlockId, setSelectedTableBlockId] = useState<string | null>(null);
  const [selectedLinkData, setSelectedLinkData] = useState<{ url: string; title: string } | null>(null);

  // Top Floating Toolbars Dock State (Format, Insert, AI, Voice)
  const [activeTopTab, setActiveTopTab] = useState<"format" | "insert" | "ai" | "capture">("format");
  const [captureMode, setCaptureMode] = useState<CaptureMode>("keyboard");
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);

  // Discrete Word & Reading-Time Counter State
  const [counterMode, setCounterMode] = useState<"words" | "chars" | "readingTime">("words");

  // Slash Command Palette State
  const [isSlashMenuOpen, setIsSlashMenuOpen] = useState(false);

  // Link Insertion State
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  // Standard Platform Pickers Modal States
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);
  const [isGroupPickerOpen, setIsGroupPickerOpen] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [isMemoryPickerOpen, setIsMemoryPickerOpen] = useState(false);

  // Collaborative User Presence & Awareness
  const { user } = useAuth();
  const [collaborators, setCollaborators] = useState<ICollaboratorState[]>([]);
  const [connected, setConnected] = useState(false);

  const userName = user?.displayName || user?.email?.split("@")[0] || "Archivist";
  const userColor = useMemo(
    () => getCollaboratorColor(user?.uid || user?.email || "anonymous"),
    [user?.uid, user?.email]
  );

  // Yjs document and WebSocket provider setup
  const { ydoc, provider } = useMemo(() => {
    if (!isCollabActive) {
      return { ydoc: null, provider: null };
    }
    const doc = new Y.Doc();
    const roomName = resolvedEntityId
      ? `${resolvedEntityType}-${resolvedEntityId}`
      : `${resolvedEntityType}-scratch-${Date.now()}`;
    const wsUrl = getCollabWebSocketUrl();
    let wsProvider: WebsocketProvider | null = null;
    if (wsUrl) {
      try {
        wsProvider = new WebsocketProvider(wsUrl, roomName, doc, { connect: true });
      } catch (e) {
        console.warn("[ThemedEditor] Could not connect to collaborative WebSocket server:", e);
      }
    }
    return { ydoc: doc, provider: wsProvider };
  }, [isCollabActive, resolvedEntityId, resolvedEntityType]);

  // Clean up Yjs doc and provider on unmount
  useEffect(() => {
    return () => {
      provider?.destroy();
      ydoc?.destroy();
    };
  }, [ydoc, provider]);

  // Update Yjs Awareness user presence whenever user identity or color changes
  useEffect(() => {
    if (!provider?.awareness) return;
    provider.awareness.setLocalStateField("user", {
      name: userName,
      color: userColor,
      email: user?.email,
      uid: user?.uid,
    });
  }, [provider, userName, userColor, user?.email, user?.uid]);

  // Wire up Yjs Awareness Protocol listener for remote collaborators
  useEffect(() => {
    if (!provider) return;
    const awareness = provider.awareness;
    const updatePresence = () => {
      const states = Array.from(awareness.getStates().entries());
      const active: ICollaboratorState[] = [];
      states.forEach(([clientId, state]: [number, any]) => {
        if (state.user && state.user.name) {
          active.push({
            clientId,
            name: state.user.name,
            color: state.user.color || "#D97706",
            email: state.user.email,
            uid: state.user.uid,
          });
        }
      });
      setCollaborators(active);
    };

    const handleStatus = (event: any) => {
      setConnected(event.status === "connected");
    };

    awareness.on("change", updatePresence);
    provider.on("status", handleStatus);
    return () => {
      awareness.off("change", updatePresence);
      provider.off("status", handleStatus);
    };
  }, [provider]);

  // Editor root container reference for positioning calculations
  const editorContainerRef = useRef<View>(null);
  const contentEditableFlowRef = useRef<View>(null);

  // Synchronize document across Yjs CRDT
  const ytextRef = React.useRef<Y.Text | null>(null);
  const isRemoteUpdatingRef = React.useRef(false);
  const hasLoadedInitialRef = React.useRef(false);

  useEffect(() => {
    if (!ydoc) {
      ytextRef.current = null;
      return;
    }
    const ytext = ydoc.getText("html");
    ytextRef.current = ytext;

    const handleYTextChange = () => {
      if (isRemoteUpdatingRef.current) return;
      const remoteHtml = ytext.toString();
      if (!remoteHtml) return;

      const currentSerialized = serializeBlocksToHtml(blocks);
      if (remoteHtml !== currentSerialized) {
        isRemoteUpdatingRef.current = true;
        setBlocks(parseHtmlToBlocks(remoteHtml));
        onChange?.(remoteHtml);
        setTimeout(() => {
          isRemoteUpdatingRef.current = false;
        }, 50);
      }
    };

    ytext.observe(handleYTextChange);

    if (provider) {
      const handleSync = (isSynced: boolean) => {
        if (isSynced && !hasLoadedInitialRef.current) {
          hasLoadedInitialRef.current = true;
          if (ytext.length > 0) {
            const syncedHtml = ytext.toString();
            setBlocks(parseHtmlToBlocks(syncedHtml));
            onChange?.(syncedHtml);
          } else if (resolvedContent) {
            ydoc.transact(() => {
              ytext.insert(0, resolvedContent);
            });
          }
        }
      };
      provider.on("sync", handleSync);
      const fallbackTimer = setTimeout(() => {
        if (!hasLoadedInitialRef.current) {
          hasLoadedInitialRef.current = true;
          if (ytext.length > 0) {
            const syncedHtml = ytext.toString();
            setBlocks(parseHtmlToBlocks(syncedHtml));
            onChange?.(syncedHtml);
          } else if (resolvedContent) {
            ydoc.transact(() => {
              ytext.insert(0, resolvedContent);
            });
          }
        }
      }, 500);

      return () => {
        ytext.unobserve(handleYTextChange);
        provider.off("sync", handleSync);
        clearTimeout(fallbackTimer);
      };
    } else {
      if (resolvedContent && ytext.length === 0) {
        ydoc.transact(() => {
          ytext.insert(0, resolvedContent);
        });
      }
      return () => {
        ytext.unobserve(handleYTextChange);
      };
    }
  }, [ydoc, provider, resolvedContent, onChange]);

  const isLocalInputRef = useRef(false);
  const lastUndoPushRef = useRef<number>(0);

  // Sync external prop changes when value updates externally and not updating from Yjs
  useEffect(() => {
    if (isLocalInputRef.current) {
      isLocalInputRef.current = false;
      return;
    }
    const currentSerialized = serializeBlocksToHtml(blocks);
    if (resolvedContent && resolvedContent !== currentSerialized && !isRemoteUpdatingRef.current) {
      const parsed = parseHtmlToBlocks(resolvedContent);
      setBlocks(parsed);
      flushFlowNodes(parsed);
    }
  }, [resolvedContent, flushFlowNodes]);

  // Sync mode changes
  useEffect(() => {
    if (mode === "view") {
      setActiveTab("view");
      setActiveToolbar("none");
    } else if (mode === "edit") {
      setActiveTab("edit");
      setActiveToolbar("text");
    }
  }, [mode]);

  // Theme tokens
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const cardBg = useThemeColor({}, "card");
  const textMuted = useThemeColor({}, "textMuted");
  const textColor = useThemeColor({}, "text");

  // Partition blocks into media, tables, and text
  const mediaBlocks = useMemo(() => blocks.filter((b) => b.type === "media" && b.media), [blocks]);
  const tableBlocks = useMemo(() => blocks.filter((b) => b.type === "table"), [blocks]);
  const textBlocks = useMemo(() => blocks.filter((b) => b.type !== "media" && b.type !== "table"), [blocks]);

  // Primary text block for formatting and styling
  const primaryTextBlockIndex = useMemo(() => {
    const idx = blocks.findIndex((b) => b.type !== "media" && b.type !== "table");
    return idx >= 0 ? idx : 0;
  }, [blocks]);

  const currentBlock = blocks[focusedBlockIndex] || blocks[primaryTextBlockIndex] || blocks[0] || null;

  // Single continuous text representation across all text blocks
  const unifiedText = useMemo(() => {
    return textBlocks.map((b) => b.text).filter(Boolean).join("\n\n");
  }, [textBlocks]);

  // Normal text editing: strip any angle brackets from text displayed in TextInput
  const displayEditorText = useMemo(() => {
    return stripHtmlTags(unifiedText);
  }, [unifiedText]);

  // Normalized selection boundaries
  const hasSelection = selection.start !== selection.end && Math.abs(selection.end - selection.start) > 0;
  const selStart = Math.min(selection.start, selection.end);
  const selEnd = Math.max(selection.start, selection.end);

  // Refs to preserve and restore browser text selection across format application
  const isApplyingFormatRef = useRef(false);
  const pendingSelectionRestoreRef = useRef<{ start: number; end: number } | null>(null);

  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  // Helper to ensure floating toolbar is displayed and sync web text selection
  const updateWebSelectionPosition = useCallback(() => {
    // If a formatting change is currently being applied, do not let intermediate browser selection loss overwrite selection state
    if (isApplyingFormatRef.current) {
      return;
    }

    // Edit Toolbar floats stably at the top of ThemedEditor and does not reposition on cursor movements
    if (activeToolbar !== "text") {
      setActiveToolbar("text");
    }

    if (Platform.OS === "web" && typeof window !== "undefined") {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const container = (contentEditableFlowRef.current as any) || (editorContainerRef.current as any);
        if (container && container.contains && container.contains(range.commonAncestorContainer)) {
          const resStart = extractEditorPlainTextAndOffset(container, range.startContainer, range.startOffset);
          const plainStart = resStart.caretOffset;

          const resEnd = extractEditorPlainTextAndOffset(container, range.endContainer, range.endOffset);
          const plainEnd = resEnd.caretOffset;

          // Map plain text character offsets to HTML/tagged offsets in unifiedText
          const start = mapPlainOffsetToHtmlOffset(unifiedText, plainStart);
          const end = mapPlainOffsetToHtmlOffset(unifiedText, plainEnd);

          if (selectionRef.current.start !== start || selectionRef.current.end !== end) {
            selectionRef.current = { start, end };
            setSelection({ start, end });
            onSelectionChange?.({ start, end });
          }
        }
      }
    }
  }, [activeToolbar, onSelectionChange, unifiedText]);

  // Restore DOM text selection after React re-renders formatted HTML in contentEditable
  useEffect(() => {
    if (Platform.OS === "web" && pendingSelectionRestoreRef.current) {
      if (isTypingRef.current) {
        return;
      }
      const targetSel = pendingSelectionRestoreRef.current;
      pendingSelectionRestoreRef.current = null;

      const container = (contentEditableFlowRef.current as any) || (editorContainerRef.current as any);
      if (container) {
        const plainStart = mapHtmlOffsetToPlainOffset(unifiedText, targetSel.start);
        const plainEnd = mapHtmlOffsetToPlainOffset(unifiedText, targetSel.end);
        restoreDomSelection(container, plainStart, plainEnd);
      }

      // Allow DOM to settle before re-enabling selectionchange listener processing
      const timer = setTimeout(() => {
        isApplyingFormatRef.current = false;
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [unifiedText, blocks, flowNodes]);

  // Listen to web document selection changes with RAF batching to keep React selection state in sync without forced reflow
  useEffect(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      let rafId: number | null = null;
      const handleSelectionChange = () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          updateWebSelectionPosition();
          rafId = null;
        });
      };
      document.addEventListener("selectionchange", handleSelectionChange);
      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        document.removeEventListener("selectionchange", handleSelectionChange);
      };
    }
  }, [updateWebSelectionPosition]);

  // All attached chips across text blocks
  const allChips = useMemo(() => {
    const list: { chip: IEditorChipToken; blockIndex: number }[] = [];
    const seen = new Set<string>();
    blocks.forEach((b, bIdx) => {
      if (b.chips) {
        b.chips.forEach((c) => {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            list.push({ chip: c, blockIndex: bIdx });
          }
        });
      }
    });
    return list;
  }, [blocks]);

  // Helper to count total entity chips across blocks to detect entity addition/removal
  const countTotalChips = (blockList: IEditorBlock[]): number => {
    let count = 0;
    for (let i = 0; i < blockList.length; i++) {
      if (blockList[i].chips) {
        count += blockList[i].chips!.length;
      }
    }
    return count;
  };

  // Emit HTML changes and sync with collaborative Yjs document
  const notifyChanges = useCallback((updatedBlocks: IEditorBlock[]) => {
    // Record previous blocks onto undo stack (throttled during continuous typing to avoid JSON cloning lag)
    const now = Date.now();
    if (now - lastUndoPushRef.current > 600 || updatedBlocks.length !== blocks.length) {
      lastUndoPushRef.current = now;
      if (blocks && blocks.length > 0) {
        undoStackRef.current.push(JSON.parse(JSON.stringify(blocks)));
        if (undoStackRef.current.length > 50) undoStackRef.current.shift();
        redoStackRef.current = [];
        setCanUndo(true);
        setCanRedo(false);
      }
    }

    setBlocks(updatedBlocks);
    isLocalInputRef.current = true;

    // Check if structural changes occurred (blocks added/removed, chips count changed, empty editor transition)
    const prevChipCount = countTotalChips(blocks);
    const nextChipCount = countTotalChips(updatedBlocks);
    const isStructural =
      updatedBlocks.length !== blocks.length ||
      prevChipCount !== nextChipCount ||
      flowNodes.length === 0;

    if (isStructural) {
      flushFlowNodes(updatedBlocks);
    } else {
      scheduleDebouncedFlowNodes(updatedBlocks);
    }

    const serialized = serializeBlocksToHtml(updatedBlocks);
    if (onChange) {
      onChange(serialized);
    }
    if (ydoc && ytextRef.current && !isRemoteUpdatingRef.current) {
      const ytext = ytextRef.current;
      if (ytext.toString() !== serialized) {
        isRemoteUpdatingRef.current = true;
        ydoc.transact(() => {
          ytext.delete(0, ytext.length);
          ytext.insert(0, serialized);
        });
        setTimeout(() => {
          isRemoteUpdatingRef.current = false;
        }, 50);
      }
    }
  }, [blocks, onChange, ydoc, flowNodes.length, flushFlowNodes, scheduleDebouncedFlowNodes]);

  // Undo and Redo Action Handlers
  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    redoStackRef.current.push(JSON.parse(JSON.stringify(blocks)));
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    setBlocks(previous);
    flushFlowNodes(previous);
    const serialized = serializeBlocksToHtml(previous);
    onChange?.(serialized);
  }, [blocks, onChange, flushFlowNodes]);

  const handleRedo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push(JSON.parse(JSON.stringify(blocks)));
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
    setBlocks(next);
    flushFlowNodes(next);
    const serialized = serializeBlocksToHtml(next);
    onChange?.(serialized);
  }, [blocks, onChange, flushFlowNodes]);

  // Single text change handler with clean normal text editing (preserves multi-block structure and safe formatting tags)
  const handleUnifiedTextChange = (newText: string) => {
    // 1. Check for Slash Commands trigger (e.g. typing "/" on a blank line or after a space)
    if (newText.endsWith(" /") || newText === "/") {
      setIsSlashMenuOpen(true);
    } else if (isSlashMenuOpen && !newText.includes("/")) {
      setIsSlashMenuOpen(false);
    }

    const cleanText = sanitizeFormattingHtml(newText);
    const textToApply = cleanText
      .replace(/-- /g, "— ")
      .replace(/-> /g, "→ ")
      .replace(/<- /g, "← ")
      .replace(/\b1\/2\s/g, "½ ")
      .replace(/\b1\/4\s/g, "¼ ")
      .replace(/\b3\/4\s/g, "¾ ")
      .replace(/\.\.\.\s/g, "… ");
    const textBlockIndices: number[] = [];
    blocks.forEach((b, idx) => {
      if (b.type !== "media" && b.type !== "table") {
        textBlockIndices.push(idx);
      }
    });

    if (textBlockIndices.length === 0) {
      const updated = [
        ...blocks,
        {
          id: `b-${Date.now()}`,
          type: "paragraph" as EditorBlockType,
          text: textToApply,
        },
      ];
      notifyChanges(updated);
      return;
    }

    if (textBlockIndices.length === 1) {
      const updated = [...blocks];
      const targetIdx = textBlockIndices[0];
      updated[targetIdx] = {
        ...updated[targetIdx],
        text: textToApply,
      };
      notifyChanges(updated);
      return;
    }

    // Multi-block document (e.g. h3, paragraph, paragraph, quote)
    const chunks = textToApply.split(/(?:\r?\n){2,}/);
    const updated = [...blocks];

    if (chunks.length === textBlockIndices.length) {
      // 1-to-1 mapping: each block preserves its id, type, formats, and chips while updating its text
      textBlockIndices.forEach((blockIdx, i) => {
        updated[blockIdx] = {
          ...updated[blockIdx],
          text: chunks[i],
        };
      });
      notifyChanges(updated);
    } else if (chunks.length < textBlockIndices.length) {
      // Paragraphs were merged or removed
      const updatedBlocks: IEditorBlock[] = [];
      let textChunkIdx = 0;
      blocks.forEach((b) => {
        if (b.type === "media" || b.type === "table") {
          updatedBlocks.push(b);
        } else if (textChunkIdx < chunks.length) {
          updatedBlocks.push({
            ...b,
            text: chunks[textChunkIdx++],
          });
        }
      });
      notifyChanges(updatedBlocks);
    } else {
      // Paragraphs were added / split
      const updatedBlocks: IEditorBlock[] = [];
      let textChunkIdx = 0;
      blocks.forEach((b) => {
        if (b.type === "media" || b.type === "table") {
          updatedBlocks.push(b);
        } else if (textChunkIdx < textBlockIndices.length) {
          updatedBlocks.push({
            ...b,
            text: chunks[textChunkIdx++],
          });
        }
      });
      while (textChunkIdx < chunks.length) {
        updatedBlocks.push({
          id: `b-${Date.now()}-${textChunkIdx}`,
          type: "paragraph",
          text: chunks[textChunkIdx++],
        });
      }
      notifyChanges(updatedBlocks);
    }
  };

  // Cursor-Position Insertion Helper
  const insertAtCursor = (contentToInsert: string, chip?: IEditorChipToken) => {
    const before = unifiedText.substring(0, selStart);
    const after = unifiedText.substring(selEnd);
    const updatedText = before + contentToInsert + after;

    const textBlockIndices: number[] = [];
    blocks.forEach((b, idx) => {
      if (b.type !== "media" && b.type !== "table") {
        textBlockIndices.push(idx);
      }
    });

    if (chip && textBlockIndices.length > 0) {
      let currentOffset = 0;
      let targetBlockIdx = textBlockIndices[0];
      for (const bIdx of textBlockIndices) {
        const bTextLen = (blocks[bIdx].text || "").length;
        if (selStart <= currentOffset + bTextLen + 2) {
          targetBlockIdx = bIdx;
          break;
        }
        currentOffset += bTextLen + 2;
      }
      const updated = [...blocks];
      const target = updated[targetBlockIdx];
      const existingChips = (target.chips || []).filter((c) => c.id !== chip.id);
      const updatedChips = [...existingChips, chip];
      updated[targetBlockIdx] = {
        ...target,
        chips: updatedChips,
      };
      const cleanText = sanitizeFormattingHtml(updatedText);
      const textToApply = cleanText
        .replace(/-- /g, "— ")
        .replace(/-> /g, "→ ")
        .replace(/<- /g, "← ")
        .replace(/\b1\/2\s/g, "½ ")
        .replace(/\b1\/4\s/g, "¼ ")
        .replace(/\b3\/4\s/g, "¾ ")
        .replace(/\.\.\.\s/g, "… ");
      const chunks = textToApply.split(/(?:\r?\n){2,}/);
      if (chunks.length === textBlockIndices.length) {
        textBlockIndices.forEach((bIdx, i) => {
          updated[bIdx] = {
            ...updated[bIdx],
            text: chunks[i],
          };
        });
        notifyChanges(updated);
      } else {
        handleUnifiedTextChange(updatedText);
      }
    } else {
      handleUnifiedTextChange(updatedText);
    }

    const nextPos = selStart + contentToInsert.length;
    setSelection({ start: nextPos, end: nextPos });
    pendingSelectionRestoreRef.current = { start: nextPos, end: nextPos };
    isApplyingFormatRef.current = true;
  };

  // Document word, character, and reading-time statistics
  const docStats = useMemo(() => {
    const plain = stripHtmlTags(unifiedText).trim();
    const chars = plain.length;
    const words = plain ? plain.split(/\s+/).filter(Boolean).length : 0;
    const readingTime = Math.ceil(words / 200);
    return {
      chars,
      words,
      readingTime: readingTime === 0 && words > 0 ? 1 : readingTime,
    };
  }, [unifiedText]);

  // Slash Commands execution helper
  const handleExecuteSlashCommand = (cmd: string) => {
    setIsSlashMenuOpen(false);
    let baseText = unifiedText;
    if (baseText.endsWith(" /")) {
      baseText = baseText.substring(0, baseText.length - 2);
    } else if (baseText.endsWith("/")) {
      baseText = baseText.substring(0, baseText.length - 1);
    }

    switch (cmd) {
      case "h1":
        setBlockType("h1");
        handleUnifiedTextChange(baseText);
        break;
      case "h2":
        setBlockType("h2");
        handleUnifiedTextChange(baseText);
        break;
      case "h3":
        setBlockType("h3");
        handleUnifiedTextChange(baseText);
        break;
      case "quote":
        setBlockType("quote");
        handleUnifiedTextChange(baseText);
        break;
      case "contact":
        handleUnifiedTextChange(baseText);
        setIsContactPickerOpen(true);
        break;
      case "group":
        handleUnifiedTextChange(baseText);
        setIsGroupPickerOpen(true);
        break;
      case "media":
        handleUnifiedTextChange(baseText);
        setIsMediaPickerOpen(true);
        break;
      case "memory":
        handleUnifiedTextChange(baseText);
        setIsMemoryPickerOpen(true);
        break;
      case "table":
        handleUnifiedTextChange(baseText);
        handleInsertTable();
        break;
      case "weblink":
        handleUnifiedTextChange(baseText);
        setActiveToolbar("link");
        break;
      case "voice":
        handleUnifiedTextChange(baseText);
        setActiveTopTab("capture");
        handleToggleVoiceRecording();
        break;
      default:
        handleUnifiedTextChange(baseText);
        break;
    }
  };

  // Toggle Voice Dictation Simulation
  const handleToggleVoiceRecording = () => {
    if (isVoiceRecording) {
      setIsVoiceRecording(false);
    } else {
      setIsVoiceRecording(true);
      setTimeout(() => {
        const voiceSnippet = " [Voice dictation: Golden coastal afternoon with friends]";
        insertAtCursor(voiceSnippet);
        setIsVoiceRecording(false);
      }, 2500);
    }
  };

  // AI Actions Handler
  const handleAiAction = (actionName: string) => {
    if (actionName === "enhance") {
      const selected = hasSelection ? unifiedText.substring(selStart, selEnd) : unifiedText;
      if (!selected.trim()) return;
      const enhanced = selected
        .replace(/\bvery good\b/gi, "exceptional")
        .replace(/\bnice\b/gi, "delightful")
        .replace(/\bbig\b/gi, "momentous");
      if (hasSelection) {
        const before = unifiedText.substring(0, selStart);
        const after = unifiedText.substring(selEnd);
        handleUnifiedTextChange(before + enhanced + after);
      } else {
        handleUnifiedTextChange(enhanced);
      }
    } else if (actionName === "autocomplete") {
      const completion = " and captured timeless moments we will cherish forever.";
      insertAtCursor(completion);
    } else if (actionName === "spellcheck") {
      const corrected = unifiedText
        .replace(/\bteh\b/gi, "the")
        .replace(/\brecieve\b/gi, "receive");
      handleUnifiedTextChange(corrected);
    } else if (actionName === "story") {
      const storyBlock = "<h3>The Story Behind This Memory</h3><p>Every photograph holds a quiet story waiting to be told...</p>";
      insertAtCursor("\n\n" + storyBlock + "\n\n");
    } else if (actionName === "translate") {
      insertAtCursor(" [Translated: Una tarde soleada inolvidable]");
    }
  };

  // Toggle inline format (bold, italic, underline, strike) applied into HTML rather than raw tags in editor
  const toggleInlineFormat = (formatKey: keyof IEditorFormatState) => {
    const targetIdx = focusedBlockIndex >= 0 && blocks[focusedBlockIndex] ? focusedBlockIndex : primaryTextBlockIndex;

    if (hasSelection) {
      // 1. FORMAT SPECIFICALLY SELECTED TEXT
      const selected = unifiedText.substring(selStart, selEnd);
      const before = unifiedText.substring(0, selStart);
      const after = unifiedText.substring(selEnd);

      const tagMap: Record<string, string> = {
        bold: "b",
        italic: "i",
        underline: "u",
        strike: "s",
      };
      const tag = tagMap[formatKey] || "b";
      const tagOpenRegex = new RegExp(`^<${tag}>([\\s\\S]*?)<\\/${tag}>$`, "i");

      let nextText = "";
      let newStart = selStart;
      let newEnd = selEnd;

      let formatted = selected;
      // Case A: Surrounding text has the tag (e.g. before ends with <b> and after starts with </b>)
      const openTagStr = `<${tag}>`;
      const closeTagStr = `</${tag}>`;
      if (
        before.toLowerCase().endsWith(openTagStr) &&
        after.toLowerCase().startsWith(closeTagStr)
      ) {
        // Toggle off surrounding tag
        const newBefore = before.substring(0, before.length - openTagStr.length);
        const newAfter = after.substring(closeTagStr.length);
        nextText = newBefore + selected + newAfter;
        newStart = selStart - openTagStr.length;
        newEnd = newStart + selected.length;
      } else if (tagOpenRegex.test(selected.trim())) {
        // Case B: Selected text itself has the wrapping tag
        formatted = selected.replace(new RegExp(`^<${tag}>|<\\/${tag}>$`, "gi"), "");
        nextText = before + formatted + after;
        newStart = selStart;
        newEnd = selStart + formatted.length;
      } else {
        // Case C: Toggle on
        formatted = `<${tag}>${selected}</${tag}>`;
        nextText = before + formatted + after;
        newStart = selStart;
        newEnd = selStart + formatted.length;
      }

      const newSel = { start: newStart, end: newEnd };
      pendingSelectionRestoreRef.current = newSel;
      isApplyingFormatRef.current = true;
      handleUnifiedTextChange(nextText);

      // Maintain selection of formatted text
      setSelection({ start: selStart, end: newEnd });
    } else {
      // 2. NO TEXT SELECTED: DO NOT MODIFY WHOLE TEXT BOX OR BLOCK FORMATS!
      // Solely set the cursor text format (typingFormats) so subsequent typed characters receive the format
      setTypingFormats((prev) => ({
        ...prev,
        [formatKey]: !prev[formatKey],
      }));
      setSelection({ start: selStart, end: selEnd });
    }
  };

  // Keyboard Shortcuts Handler (Ctrl+B, Ctrl+I, Ctrl+U, Ctrl+K / Cmd+ equivalents)
  const handleEditorKeyDown = (e: any) => {
    if (Platform.OS !== "web") return;
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;
    if (!isCtrlOrCmd) return;

    const key = e.key ? e.key.toLowerCase() : "";
    if (key === "z") {
      e.preventDefault();
      if (e.shiftKey) {
        handleRedo();
      } else {
        handleUndo();
      }
    } else if (key === "y") {
      e.preventDefault();
      handleRedo();
    } else if (key === "b") {
      e.preventDefault();
      toggleInlineFormat("bold");
    } else if (key === "i") {
      e.preventDefault();
      toggleInlineFormat("italic");
    } else if (key === "u") {
      e.preventDefault();
      toggleInlineFormat("underline");
    } else if (key === "k") {
      e.preventDefault();
      // Open LinkFormatToolbar popup
      if (hasSelection) {
        const selected = unifiedText.substring(selStart, selEnd);
        setLinkTitle(selected);
      }
      setActiveToolbar("link");
    }
  };

  // Remove specific format from typingFormats, selection, or current block
  const removeFormat = (formatKey: keyof IEditorFormatState) => {
    if (typingFormats[formatKey] !== undefined) {
      setTypingFormats((prev) => {
        const next = { ...prev };
        delete next[formatKey];
        return next;
      });
    }

    const targetIdx = focusedBlockIndex >= 0 && blocks[focusedBlockIndex] ? focusedBlockIndex : primaryTextBlockIndex;
    const updatedBlocks = [...blocks];
    if (updatedBlocks[targetIdx] && updatedBlocks[targetIdx].formats) {
      const currentFormats = { ...updatedBlocks[targetIdx].formats };
      delete currentFormats[formatKey];
      updatedBlocks[targetIdx] = {
        ...updatedBlocks[targetIdx],
        formats: Object.keys(currentFormats).length > 0 ? currentFormats : undefined,
      };
      notifyChanges(updatedBlocks);
    }
    setSelection({ start: selStart, end: selEnd });
  };

  // Set or toggle font family on selection or arm typingFormats
  const setFontFamily = (family?: string) => {
    const targetIdx = focusedBlockIndex >= 0 && blocks[focusedBlockIndex] ? focusedBlockIndex : primaryTextBlockIndex;

    if (hasSelection) {
      const selected = unifiedText.substring(selStart, selEnd);
      let formatted = selected;
      if (family && family !== "sans") {
        formatted = `<span data-font="${family}">${selected.replace(/<span data-font="[^"]*">|<\/span>/gi, "")}</span>`;
      } else {
        formatted = selected.replace(/<span data-font="[^"]*">|<\/span>/gi, "");
      }
      const nextText = unifiedText.substring(0, selStart) + formatted + unifiedText.substring(selEnd);
      const newSel = { start: selStart, end: selStart + formatted.length };
      pendingSelectionRestoreRef.current = newSel;
      isApplyingFormatRef.current = true;
      handleUnifiedTextChange(nextText);
      setSelection({ start: selStart, end: selStart + formatted.length });
    } else {
      const updated = [...blocks];
      if (updated[targetIdx]) {
        const currentFormats = { ...(updated[targetIdx].formats || {}) };
        if (family && family !== "sans") {
          currentFormats.fontFamily = family;
        } else {
          delete currentFormats.fontFamily;
        }
        updated[targetIdx] = {
          ...updated[targetIdx],
          formats: Object.keys(currentFormats).length > 0 ? currentFormats : undefined,
        };
        notifyChanges(updated);
      }
      setTypingFormats((prev) => ({
        ...prev,
        fontFamily: prev.fontFamily === family ? undefined : family,
      }));
      setSelection({ start: selStart, end: selEnd });
    }
  };

  // Set or toggle font size on selection or arm typingFormats
  const setFontSize = (size?: string) => {
    const targetIdx = focusedBlockIndex >= 0 && blocks[focusedBlockIndex] ? focusedBlockIndex : primaryTextBlockIndex;

    if (hasSelection) {
      const selected = unifiedText.substring(selStart, selEnd);
      let formatted = selected;
      if (size && size !== "md") {
        formatted = `<span data-size="${size}">${selected.replace(/<span data-size="[^"]*">|<\/span>/gi, "")}</span>`;
      } else {
        formatted = selected.replace(/<span data-size="[^"]*">|<\/span>/gi, "");
      }
      const nextText = unifiedText.substring(0, selStart) + formatted + unifiedText.substring(selEnd);
      const newSel = { start: selStart, end: selStart + formatted.length };
      pendingSelectionRestoreRef.current = newSel;
      isApplyingFormatRef.current = true;
      handleUnifiedTextChange(nextText);
      setSelection(newSel);
    } else {
      const updated = [...blocks];
      if (updated[targetIdx]) {
        const currentFormats = { ...(updated[targetIdx].formats || {}) };
        if (size && size !== "md") {
          currentFormats.fontSize = size;
        } else {
          delete currentFormats.fontSize;
        }
        updated[targetIdx] = {
          ...updated[targetIdx],
          formats: Object.keys(currentFormats).length > 0 ? currentFormats : undefined,
        };
        notifyChanges(updated);
      }
      setTypingFormats((prev) => ({
        ...prev,
        fontSize: prev.fontSize === size ? undefined : size,
      }));
      setSelection({ start: selStart, end: selEnd });
    }
  };

  // Change block style type (paragraph, h1, h2, h3, bullet-list, quote)
  const setBlockType = (newType: EditorBlockType) => {
    if (!currentBlock) return;
    const targetIdx = focusedBlockIndex >= 0 && blocks[focusedBlockIndex] ? focusedBlockIndex : primaryTextBlockIndex;
    const updated = [...blocks];
    updated[targetIdx] = {
      ...blocks[targetIdx],
      type: newType,
    };
    if (hasSelection) {
      pendingSelectionRestoreRef.current = { start: selStart, end: selEnd };
      isApplyingFormatRef.current = true;
    }
    notifyChanges(updated);
  };

  // Clear all formats from typingFormats, selection, and current block
  const handleClearFormatting = () => {
    setTypingFormats({});

    if (hasSelection) {
      const selected = unifiedText.substring(selStart, selEnd);
      const cleaned = selected
        .replace(/<\/?(b|strong|i|em|u|s|strike)>/gi, "")
        .replace(/<span data-(font|size)="[^"]*">/gi, "")
        .replace(/<\/span>/gi, "");
      const nextText = unifiedText.substring(0, selStart) + cleaned + unifiedText.substring(selEnd);
      const newSel = { start: selStart, end: selStart + cleaned.length };
      pendingSelectionRestoreRef.current = newSel;
      isApplyingFormatRef.current = true;
      handleUnifiedTextChange(nextText);
      setSelection(newSel);
    }

    if (currentBlock) {
      const targetIdx = focusedBlockIndex >= 0 && blocks[focusedBlockIndex] ? focusedBlockIndex : primaryTextBlockIndex;
      const updated = [...blocks];
      updated[targetIdx] = {
        ...blocks[targetIdx],
        type: "paragraph",
        formats: undefined,
      };
      notifyChanges(updated);
    }
  };

  // Insert Table Block
  const handleInsertTable = () => {
    const tableBlock: IEditorBlock = {
      id: `table-${Date.now()}`,
      type: "table",
      text: "",
      table: {
        headers: ["Header 1", "Header 2", "Header 3"],
        rows: [
          ["Cell 1", "Cell 2", "Cell 3"],
          ["Cell 4", "Cell 5", "Cell 6"],
        ],
      },
    };
    const updated = [...blocks, tableBlock];
    notifyChanges(updated);
  };

  // Remove block by index or id
  const handleRemoveBlock = (index: number) => {
    if (blocks.length <= 1) {
      const reset = [{ id: `b-${Date.now()}-0`, type: "paragraph" as EditorBlockType, text: "" }];
      setFocusedBlockIndex(0);
      notifyChanges(reset);
      return;
    }
    const updated = blocks.filter((_, i) => i !== index);
    setFocusedBlockIndex(Math.max(0, index - 1));
    notifyChanges(updated);
  };

  const handleRemoveBlockById = (blockId: string) => {
    const updated = blocks.filter((b) => b.id !== blockId);
    if (updated.length === 0) {
      updated.push({ id: `b-${Date.now()}-0`, type: "paragraph", text: "" });
    }
    notifyChanges(updated);
  };

  // Remove inline mention chip from block
  const handleRemoveChip = (blockIndex: number, chipId: string) => {
    const block = blocks[blockIndex];
    if (!block || !block.chips) return;
    const updatedChips = block.chips.filter((c) => c.id !== chipId);
    const updated = [...blocks];
    updated[blockIndex] = {
      ...block,
      chips: updatedChips.length > 0 ? updatedChips : undefined,
    };
    notifyChanges(updated);
  };

  // Insert Link at cursor position
  const handleInsertLinkSubmit = () => {
    if (!linkUrl.trim()) return;
    const title = linkTitle.trim() || linkUrl.trim();
    const linkMarkup = `<a href="${linkUrl.trim()}" title="${escapeHtml(title)}">${escapeHtml(title)}</a>`;
    insertAtCursor(linkMarkup);

    setLinkTitle("");
    setLinkUrl("");
    setIsLinkModalOpen(false);
    setActiveToolbar("none");
  };

  // Unlink helper for LinkFormatToolbar
  const handleUnlink = () => {
    if (hasSelection) {
      const selected = unifiedText.substring(selStart, selEnd);
      const unlinked = selected.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, "$1");
      const nextText = unifiedText.substring(0, selStart) + unlinked + unifiedText.substring(selEnd);
      handleUnifiedTextChange(nextText);
      setSelection({ start: selStart, end: selStart + unlinked.length });
    }
    setActiveToolbar("none");
  };

  /**
   * Resolves display name for an entity mention chip, prioritizing real contact/group/memory names
   * over generic placeholder labels like "Contact", "Group", or "Memory".
   */
  const resolveChipLabel = useCallback(
    (entityType?: string, id?: string, fallbackLabel?: string): string => {
      const raw = (fallbackLabel || "").trim();
      const isPlaceholder = !raw || ["contact", "group", "memory", "media"].includes(raw.toLowerCase());
      if (!isPlaceholder) return raw;

      if (entityType === "contact") {
        const c = (contacts as any[])?.find((item) => (item.id || item.contactId) === id);
        if (c) return c.displayName || c.fullName || c.name || c.email || raw || "Contact";
      } else if (entityType === "group") {
        const g = (groups as any[])?.find((item) => (item.id || item.groupId) === id);
        if (g) return g.name || g.displayName || raw || "Group";
      } else if (entityType === "memory") {
        const m = (memories as any[])?.find((item) => (item.id || item.memoryId) === id);
        if (m) return m.title || m.name || raw || "Memory";
      }
      return raw || (entityType ? `${entityType}` : "Item");
    },
    [contacts, groups, memories]
  );

  // Insert entity mention from pickers at cursor position
  const insertEntityMention = (type: MentionEntityType, id: string, label: string) => {
    const newChip: IEditorChipToken = {
      id,
      entityType: type,
      label,
      offset: selStart,
    };
    insertAtCursor(` ${label} `, newChip);
  };

  // Standard Picker Handlers
  const handleSelectContact = (contact: ContactModel) => {
    const name = contact.displayName || (contact as any).fullName || (contact as any).name || (contact as any).email || "Contact";
    insertEntityMention("contact", contact.id || `c-${Date.now()}`, name);
  };

  const handleSelectGroup = (group: GroupModel) => {
    const name = group.name || (group as any).displayName || "Group";
    insertEntityMention("group", group.id || `g-${Date.now()}`, name);
  };

  // Insert media blocks at the current cursor position in the document flow
  const insertMediaBlocksAtCursor = (newMediaBlocks: IEditorBlock[]) => {
    if (!newMediaBlocks || newMediaBlocks.length === 0) return;

    // Default media to align: "left" so text flows around it on the right!
    newMediaBlocks.forEach((mb) => {
      if (mb.media) {
        mb.media.align = mb.media.align || "left";
        mb.media.size = mb.media.size || "50%";
      }
    });

    const textBlockIndices: number[] = [];
    blocks.forEach((b, idx) => {
      if (b.type !== "media" && b.type !== "table") {
        textBlockIndices.push(idx);
      }
    });

    const targetIdx = focusedBlockIndex >= 0 ? focusedBlockIndex : primaryTextBlockIndex;

    if (textBlockIndices.length === 0) {
      const updated = [...blocks];
      if (newMediaBlocks.length === 1) {
        const mediaBlock = newMediaBlocks[0];
        updated.splice(targetIdx, 0, mediaBlock);
      } else {
        updated.splice(targetIdx, 0, ...newMediaBlocks);
      }
      notifyChanges(updated);
      return;
    }

    let cumulative = 0;
    let targetBlockIdx = targetIdx >= 0 && targetIdx < blocks.length ? targetIdx : textBlockIndices[0];
    let offsetInBlock = 0;

    for (const bIdx of textBlockIndices) {
      const bText = blocks[bIdx].text || "";
      const len = bText.length;
      if (selStart <= cumulative + len) {
        targetBlockIdx = bIdx;
        offsetInBlock = Math.max(0, selStart - cumulative);
        break;
      }
      cumulative += len + 2; // account for \n\n
      targetBlockIdx = bIdx;
      offsetInBlock = len;
    }

    const targetBlock = blocks[targetBlockIdx] || blocks[targetIdx] || blocks[0];
    const fullText = targetBlock.text || "";

    if (offsetInBlock <= 0) {
      // Cursor is at start of this text block: insert media immediately before it
      const updated = [...blocks];
      if (newMediaBlocks.length === 1) {
        const mediaBlock = newMediaBlocks[0];
        updated.splice(targetIdx, 0, mediaBlock);
      } else {
        updated.splice(targetIdx, 0, ...newMediaBlocks);
      }
      notifyChanges(updated);
      return;
    }

    if (offsetInBlock >= fullText.length) {
      // Cursor is at end of this text block: insert media immediately after it
      const updated = [...blocks];
      updated.splice(targetBlockIdx + 1, 0, ...newMediaBlocks);
      notifyChanges(updated);
      return;
    }

    // Cursor is in the middle of a sentence/paragraph!
    // Split targetBlock into before and after so media embeds right at the cursor!
    const textBefore = fullText.substring(0, offsetInBlock);
    const textAfter = fullText.substring(offsetInBlock);

    const chipsBefore = (targetBlock.chips || []).filter(
      (c) => (c.offset ?? 0) < offsetInBlock
    );
    const chipsAfter = (targetBlock.chips || [])
      .filter((c) => (c.offset ?? 0) >= offsetInBlock)
      .map((c) => ({
        ...c,
        offset: Math.max(0, (c.offset ?? 0) - offsetInBlock),
      }));

    const blockBefore: IEditorBlock = {
      ...targetBlock,
      id: `b-${Date.now()}-a`,
      text: textBefore,
      chips: chipsBefore,
    };

    const blockAfter: IEditorBlock = {
      ...targetBlock,
      id: `b-${Date.now()}-b`,
      text: textAfter,
      chips: chipsAfter,
    };

    const updated = [...blocks];
    updated.splice(targetBlockIdx, 1, blockBefore, ...newMediaBlocks, blockAfter);
    notifyChanges(updated);
  };

  const handleSelectMediaList = (selectedList: MediaModel[]) => {
    if (!selectedList || selectedList.length === 0) return;
    const newMediaBlocks: IEditorBlock[] = selectedList.map((m) => ({
      id: `media-${Date.now()}-${m.id}`,
      type: "media",
      text: m.title || (m as any).name || "Media",
      media: {
        mediaId: m.id || `m-${Date.now()}`,
        title: m.title || (m as any).name || "Media",
        size: "50%",
        align: "left",
        downloadUrl: m.downloadUrl,
        mediaType: m.contentType?.includes("video") ? "video" : "image",
      },
    }));

    const targetIdx = focusedBlockIndex >= 0 ? focusedBlockIndex : primaryTextBlockIndex;
    insertMediaBlocksAtCursor(newMediaBlocks);
  };

  const handleSelectMemory = (memory: MemoryModel) => {
    const title = memory.title || "Memory";
    insertEntityMention("memory", memory.id || `mem-${Date.now()}`, title);
  };

  // Copy/Paste Media Pipeline (inserted at cursor position)
  const handlePasteMediaFile = async (file: any) => {
    try {
      const fileName = file.name || "Pasted Media";
      const targetIdx = focusedBlockIndex >= 0 ? focusedBlockIndex : primaryTextBlockIndex;

      // 1. Check if already exists in media collection
      const existing = (media as any[]).find(
        (m) =>
          (m.name && m.name === fileName) ||
          (m.title && m.title === fileName) ||
          (m.id && m.id === file.id)
      );

      if (existing) {
        const mediaBlock: IEditorBlock = {
          id: `media-${Date.now()}`,
          type: "media",
          text: existing.title || existing.name || "Media",
          media: {
            mediaId: existing.id || "m-pasted",
            title: existing.title || existing.name || "Media",
            size: "50%",
            align: "left",
            downloadUrl: existing.downloadUrl,
          },
        };
        insertMediaBlocksAtCursor([mediaBlock]);
        return;
      }

      // 2. If not found in media, upload / register it
      if (onMediaUpload) {
        const uploadResult = await onMediaUpload(file, activeMemoryId);
        const mediaBlock: IEditorBlock = {
          id: `media-${Date.now()}`,
          type: "media",
          text: uploadResult.title,
          media: {
            mediaId: uploadResult.id,
            title: uploadResult.title,
            size: "50%",
            align: "left",
            downloadUrl: uploadResult.url,
          },
        };
        insertMediaBlocksAtCursor([mediaBlock]);
      } else {
        const fallbackId = `m-pasted-${Date.now()}`;
        const mediaBlock: IEditorBlock = {
          id: `media-${Date.now()}`,
          type: "media",
          text: fileName,
          media: {
            mediaId: fallbackId,
            title: fileName,
            size: "50%",
            align: "left",
          },
        };
        insertMediaBlocksAtCursor([mediaBlock]);
      }
    } catch (err) {
      console.warn("[ThemedEditor] Error pasting media file:", err);
    }
  };

  // Web Paste Event Listener
  const handleWebPaste = (e: any) => {
    if (Platform.OS !== "web" || !e.clipboardData) return;
    const items = e.clipboardData.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1 || item.type.indexOf("video") !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          e.preventDefault();
          handlePasteMediaFile(blob);
          break;
        }
      }
    }
  };

  // Web Drag and Drop Handler for relocating inserted items (chips, media, tables, web links)
  const handleItemDrop = (e: any) => {
    if (Platform.OS !== "web" || !editable) return;
    try {
      const dataStr =
        e.dataTransfer?.getData("application/x-editor-item") ||
        e.dataTransfer?.getData("text/plain");
      if (!dataStr) return;
      let parsedItem: any = null;
      try {
        parsedItem = JSON.parse(dataStr);
      } catch {
        return;
      }
      e.preventDefault();

      // Determine drop offset in text stream
      let dropOffset = selStart;
      if (typeof document !== "undefined" && (document as any).caretPositionFromPoint) {
        const pos = (document as any).caretPositionFromPoint(e.clientX, e.clientY);
        if (pos && typeof pos.offset === "number") dropOffset = pos.offset;
      } else if (typeof document !== "undefined" && (document as any).caretRangeFromPoint) {
        const range = (document as any).caretRangeFromPoint(e.clientX, e.clientY);
        if (range && typeof range.startOffset === "number") dropOffset = range.startOffset;
      }

      if (parsedItem.itemType === "chip") {
        // Move chip to new text location
        const chipLabel = parsedItem.label || "item";
        let textWithoutOld = unifiedText.replace(new RegExp(`\\s*${chipLabel}\\s*`, "i"), " ").trim();
        const safeDrop = Math.min(dropOffset, textWithoutOld.length);
        const newText =
          textWithoutOld.substring(0, safeDrop) +
          ` ${chipLabel} ` +
          textWithoutOld.substring(safeDrop);

        const targetIdx = primaryTextBlockIndex;
        const updated = [...blocks];
        if (updated[targetIdx]) {
          const existingChips = (updated[targetIdx].chips || []).filter(
            (c) => c.id !== parsedItem.chipId
          );
          existingChips.push({
            id: parsedItem.chipId,
            entityType: parsedItem.entityType,
            label: parsedItem.label,
            offset: safeDrop,
          });
          updated[targetIdx] = {
            ...updated[targetIdx],
            text: newText,
            chips: existingChips,
          };
          notifyChanges(updated);
        }
      } else if (parsedItem.itemType === "media") {
        // Relocate media block relative to dropped position
        const blockId = parsedItem.blockId;
        const fromIdx = blocks.findIndex(
          (b) => b.id === blockId || (b.media && b.media.mediaId === blockId)
        );
        if (fromIdx >= 0) {
          const updated = [...blocks];
          const [moved] = updated.splice(fromIdx, 1);
          if (dropOffset <= 10) {
            updated.unshift(moved);
          } else {
            updated.push(moved);
          }
          notifyChanges(updated);
        }
      } else if (parsedItem.itemType === "table") {
        // Relocate table block
        const blockId = parsedItem.blockId;
        const fromIdx = blocks.findIndex((b) => b.id === blockId);
        if (fromIdx >= 0) {
          const updated = [...blocks];
          const [moved] = updated.splice(fromIdx, 1);
          if (dropOffset <= 10) {
            updated.unshift(moved);
          } else {
            updated.push(moved);
          }
          notifyChanges(updated);
        }
      } else if (parsedItem.itemType === "link") {
        // Relocate link markup to dropped location
        const linkMarkup = `<a href="${parsedItem.url}" title="${escapeHtml(parsedItem.title || parsedItem.url)}">${escapeHtml(parsedItem.title || parsedItem.url)}</a>`;
        const textWithoutOld = unifiedText.replace(linkMarkup, "").trim();
        const safeDrop = Math.min(dropOffset, textWithoutOld.length);
        const newText =
          textWithoutOld.substring(0, safeDrop) +
          ` ${linkMarkup} ` +
          textWithoutOld.substring(safeDrop);
        handleUnifiedTextChange(newText);
      }
    } catch (err) {
      console.warn("[ThemedEditor] Drag drop error:", err);
    }
  };

  // Debounced flowNodes state is managed above to eliminate typing lag and layout reflow

  const lastInputTimestampRef = useRef<number>(0);

  // Handle contenteditable text input events on web
  const handleContentEditableInput = (e: any) => {
    if (Platform.OS !== "web") return;
    try {
      const now = Date.now();
      // Drop redundant duplicate input event fired in the same frame (native listener + synthetic onInput)
      if (now - lastInputTimestampRef.current < 40) {
        return;
      }
      lastInputTimestampRef.current = now;

      const target = (contentEditableFlowRef.current as any) || e.currentTarget || e.target;
      if (!target) return;

      let extractedText = "";
      let plainCaretOffset = 0;

      if (typeof window !== "undefined" && window.getSelection) {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          const res = extractEditorPlainTextAndOffset(target, range.startContainer, range.startOffset);
          extractedText = res.text;
          plainCaretOffset = res.caretOffset;
        } else {
          const res = extractEditorPlainTextAndOffset(target);
          extractedText = res.text;
          plainCaretOffset = res.caretOffset;
        }
      } else {
        const res = extractEditorPlainTextAndOffset(target);
        extractedText = res.text;
        plainCaretOffset = res.caretOffset;
      }

      const targetHtmlOffset = mapPlainOffsetToHtmlOffset(extractedText, plainCaretOffset);
      pendingSelectionRestoreRef.current = { start: targetHtmlOffset, end: targetHtmlOffset };
      isApplyingFormatRef.current = true;

      handleUnifiedTextChange(extractedText);
    } catch (err) {
      console.warn("[ThemedEditor] Error reading contenteditable input:", err);
    }
  };

  // Stable handler refs to prevent tearing down and re-binding listeners on every keystroke
  const inputHandlerRef = useRef(handleContentEditableInput);
  inputHandlerRef.current = handleContentEditableInput;
  const keyDownHandlerRef = useRef(handleEditorKeyDown);
  keyDownHandlerRef.current = handleEditorKeyDown;

  // Wire up true DOM contenteditable, focusability, caret styling, and event listeners on Web
  useEffect(() => {
    if (Platform.OS !== "web" || !contentEditableFlowRef.current) return;
    const dom = (contentEditableFlowRef.current as any);
    if (!dom) return;

    if (typeof dom.setAttribute === "function") {
      dom.contentEditable = editable ? "true" : "false";
      dom.setAttribute("contenteditable", editable ? "true" : "false");
      dom.tabIndex = editable ? 0 : -1;
      dom.setAttribute("role", "textbox");
      dom.setAttribute("aria-multiline", "true");
      dom.spellcheck = true;
      if (dom.style) {
        dom.style.outline = "none";
        dom.style.cursor = "text";
        dom.style.caretColor = textColor;
        dom.style.userSelect = "text";
        dom.style.minHeight = dynamicHeight ? `${floorHeight}px` : "120px";
        if (dynamicHeight && computedMaxHeight) {
          dom.style.maxHeight = `${computedMaxHeight}px`;
        }
      }
    }

    const onInput = (e: any) => {
      inputHandlerRef.current(e);
    };
    const onKeyDown = (e: any) => {
      keyDownHandlerRef.current(e);
    };

    if (typeof dom.addEventListener === "function") {
      dom.addEventListener("input", onInput);
      dom.addEventListener("keydown", onKeyDown);
      return () => {
        dom.removeEventListener("input", onInput);
        dom.removeEventListener("keydown", onKeyDown);
      };
    }
  }, [editable, textColor, dynamicHeight, floorHeight, computedMaxHeight]);

  // Webview message handler for native mobile rich flow
  const handleWebViewMessage = (event: any) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === "change" && typeof data.html === "string") {
        setBlocks(parseHtmlToBlocks(data.html));
        onChange?.(data.html);
      }
    } catch (err) {
      console.warn("[ThemedEditor] WebView message parse error:", err);
    }
  };

  // Synchronized HTML payload for mobile WebView rich text flow
  const webViewContent = useMemo(() => {
    const currentHtml = serializeBlocksToHtml(blocks);
    return `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
<style>
  body {
    margin: 0;
    padding: 8px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: ${textColor};
    background-color: transparent;
  }
  .editable-flow {
    outline: none;
    min-height: 120px;
    line-height: 1.5;
  }
  .media-embed {
    float: left;
    margin-right: 16px;
    margin-bottom: 12px;
  }
  .mention-chip {
    display: inline-flex;
    vertical-align: middle;
    margin: 0 4px;
    padding: 2px 8px;
    border-radius: 12px;
    background: rgba(5, 150, 105, 0.1);
    color: #047857;
    font-weight: 600;
    font-size: 13px;
  }
  table {
    border-collapse: collapse;
    margin: 8px 0;
    float: left;
    margin-right: 16px;
  }
  th, td {
    border: 1px solid ${borderColor};
    padding: 6px 10px;
  }
</style>
</head>
<body>
  <div id="editor" class="editable-flow" contenteditable="${editable}">
    ${currentHtml}
  </div>
  <script>
    const editor = document.getElementById('editor');
    editor.addEventListener('input', () => {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'change', html: editor.innerHTML }));
      }
    });
  </script>
</body>
</html>`;
  }, [blocks, textColor, borderColor, editable]);


  // Format active checker for toolbar buttons
  const isFormatActive = useCallback((formatKey: keyof IEditorFormatState, value?: string): boolean => {
    if (hasSelection) {
      const selected = unifiedText.substring(selStart, selEnd);
      if (formatKey === "bold") return /<b>|<strong>/i.test(selected);
      if (formatKey === "italic") return /<i>|<em>/i.test(selected);
      if (formatKey === "underline") return /<u>/i.test(selected);
      if (formatKey === "strike") return /<s>|<strike>/i.test(selected);
      if (formatKey === "fontFamily") {
        if (!value || value === "sans") return !/data-font=/i.test(selected);
        return new RegExp(`data-font=["']${value}["']`, "i").test(selected);
      }
      if (formatKey === "fontSize") {
        if (!value || value === "md") return !/data-size=/i.test(selected);
        return new RegExp(`data-size=["']${value}["']`, "i").test(selected);
      }
      return false;
    }

    // 1. Check DOM tree ancestors at current caret if on Web
    if (Platform.OS === "web" && typeof window !== "undefined") {
      const sel = window.getSelection();
      if (sel && sel.anchorNode) {
        const node = sel.anchorNode;
        const el: HTMLElement | null = node.nodeType === 1 ? (node as HTMLElement) : node.parentElement;
        if (el) {
          if (formatKey === "bold" && el.closest("b, strong")) return true;
          if (formatKey === "italic" && el.closest("i, em")) return true;
          if (formatKey === "underline" && el.closest("u")) return true;
          if (formatKey === "strike" && el.closest("s, strike")) return true;
          if (formatKey === "fontFamily") {
            const fontEl = el.closest("[data-font]");
            const fontVal = fontEl?.getAttribute("data-font") || "sans";
            return (value || "sans") === fontVal;
          }
          if (formatKey === "fontSize") {
            const sizeEl = el.closest("[data-size]");
            const sizeVal = sizeEl?.getAttribute("data-size") || "md";
            return (value || "md") === sizeVal;
          }
        }
      }
    }

    // 2. Check enclosing tags in unifiedText around selStart
    if (unifiedText && selStart >= 0) {
      const beforeText = unifiedText.substring(0, selStart);

      const checkTagEnclosed = (openRegex: RegExp, closeRegex: RegExp) => {
        const openMatches = beforeText.match(openRegex) || [];
        const closeMatches = beforeText.match(closeRegex) || [];
        return openMatches.length > closeMatches.length;
      };

      if (formatKey === "bold" && checkTagEnclosed(/<(?:b|strong)\b[^>]*>/gi, /<\/(?:b|strong)>/gi)) return true;
      if (formatKey === "italic" && checkTagEnclosed(/<(?:i|em)\b[^>]*>/gi, /<\/(?:i|em)>/gi)) return true;
      if (formatKey === "underline" && checkTagEnclosed(/<u\b[^>]*>/gi, /<\/u>/gi)) return true;
      if (formatKey === "strike" && checkTagEnclosed(/<(?:s|strike)\b[^>]*>/gi, /<\/(?:s|strike)>/gi)) return true;

      if (formatKey === "fontFamily") {
        const lastFontMatch = [...beforeText.matchAll(/<span\b[^>]*data-font=["']([^"']+)["'][^>]*>/gi)].pop();
        if (lastFontMatch) {
          const fontIndex = lastFontMatch.index ?? 0;
          const closedSince = (beforeText.substring(fontIndex).match(/<\/span>/gi) || []).length;
          if (closedSince === 0) {
            return (value || "sans") === lastFontMatch[1];
          }
        }
      }

      if (formatKey === "fontSize") {
        const lastSizeMatch = [...beforeText.matchAll(/<span\b[^>]*data-size=["']([^"']+)["'][^>]*>/gi)].pop();
        if (lastSizeMatch) {
          const sizeIndex = lastSizeMatch.index ?? 0;
          const closedSince = (beforeText.substring(sizeIndex).match(/<\/span>/gi) || []).length;
          if (closedSince === 0) {
            return (value || "md") === lastSizeMatch[1];
          }
        }
      }
    }

    if (typingFormats[formatKey] !== undefined) {
      if (formatKey === "fontFamily") return (typingFormats.fontFamily || "sans") === (value || "sans");
      if (formatKey === "fontSize") return (typingFormats.fontSize || "md") === (value || "md");
      return !!typingFormats[formatKey];
    }
    if (currentBlock?.formats?.[formatKey] !== undefined) {
      if (formatKey === "fontFamily") return (currentBlock.formats.fontFamily || "sans") === (value || "sans");
      if (formatKey === "fontSize") return (currentBlock.formats.fontSize || "md") === (value || "md");
      return !!currentBlock.formats[formatKey];
    }
    if (formatKey === "fontFamily" && (!value || value === "sans")) return true;
    if (formatKey === "fontSize" && (!value || value === "md")) return true;
    return false;
  }, [hasSelection, selStart, selEnd, unifiedText, typingFormats, currentBlock]);

  // Apply armed Format Painter formatting to active text selection
  useEffect(() => {
    if (formatPainterArmed && copiedFormats && hasSelection) {
      const target = { ...copiedFormats };
      setFormatPainterArmed(false);
      setCopiedFormats(null);
      if (target.bold) toggleInlineFormat("bold");
      if (target.italic) toggleInlineFormat("italic");
      if (target.underline) toggleInlineFormat("underline");
      if (target.strike) toggleInlineFormat("strike");
      if (target.fontFamily && target.fontFamily !== "sans") setFontFamily(target.fontFamily);
      if (target.fontSize && target.fontSize !== "md") setFontSize(target.fontSize);
    }
  }, [formatPainterArmed, copiedFormats, hasSelection]);

  // Effective active formats dynamically reflecting selected text or cursor state
  const effectiveFormats = useMemo<IEditorFormatState>(() => {
    return {
      bold: isFormatActive("bold"),
      italic: isFormatActive("italic"),
      underline: isFormatActive("underline"),
      strike: isFormatActive("strike"),
      fontFamily: isFormatActive("fontFamily", "serif")
        ? "serif"
        : isFormatActive("fontFamily", "mono")
        ? "mono"
        : isFormatActive("fontFamily", "cursive")
        ? "cursive"
        : "sans",
      fontSize: isFormatActive("fontSize", "sm")
        ? "sm"
        : isFormatActive("fontSize", "lg")
        ? "lg"
        : isFormatActive("fontSize", "xl")
        ? "xl"
        : "md",
    };
  }, [isFormatActive]);

  // Format Painter Toggle Handler
  const handleToggleFormatPainter = useCallback(() => {
    if (formatPainterArmed) {
      setFormatPainterArmed(false);
      setCopiedFormats(null);
    } else {
      setCopiedFormats({ ...effectiveFormats });
      setFormatPainterArmed(true);
    }
  }, [formatPainterArmed, effectiveFormats]);

  // View mode parsed nodes (used only when mode === "view" or editable === false)
  const viewNodes = useMemo(() => {
    const currentHtml = serializeBlocksToHtml(blocks);
    return parseHtmlNodes(currentHtml);
  }, [blocks]);

  // Resolved block style for the single text input
  const block = currentBlock || blocks[primaryTextBlockIndex] || blocks[0];

  // Memoized flow elements to prevent redrawing chips and tearing down DOM text nodes on keystrokes
  const renderedFlowNodes = useMemo(() => {
    return flowNodes.map((node, idx) => {
      if (node.type === "chip" && node.entityType && node.id) {
        const isFound = isItemFound ? isItemFound(node.entityType, node.id) : true;
        const resolvedLabel = resolveChipLabel(node.entityType, node.id, node.label);
        return (
          <View
            key={`flow-chip-${node.entityType || "item"}-${node.id || idx}`}
            ref={(domNode: any) => {
              if (domNode && Platform.OS === "web") {
                const el = domNode.nodeType ? domNode : domNode.getScrollableNode?.() || domNode;
                if (el && el.setAttribute) {
                  el.setAttribute("contenteditable", "false");
                  el.setAttribute("data-chip-label", resolvedLabel);
                  el.setAttribute("data-chip-id", node.id);
                  el.setAttribute("data-chip-type", node.entityType);
                  el.contentEditable = "false";
                }
              }
            }}
            {...(Platform.OS === "web"
              ? ({
                  contentEditable: false,
                  dataSet: {
                    chipLabel: resolvedLabel,
                    chipId: node.id,
                    chipType: node.entityType,
                  },
                  "data-chip-label": resolvedLabel,
                  "data-chip-id": node.id,
                  "data-chip-type": node.entityType,
                } as any)
              : {})}
            style={[
              styles.inlineChipWrapper,
              Platform.OS === "web" ? ({ display: "inline-flex", verticalAlign: "middle" } as any) : {},
            ]}
          >
            <ThemedChip
              entityType={node.entityType}
              id={node.id}
              label={resolvedLabel}
              draggable={editable}
              onDragStart={(e) => {
                if (Platform.OS === "web" && e?.dataTransfer) {
                  e.dataTransfer.setData(
                    "application/x-editor-item",
                    JSON.stringify({
                      itemType: "chip",
                      chipId: node.id,
                      entityType: node.entityType,
                      label: resolvedLabel,
                    })
                  );
                }
              }}
              canRemove={editable}
              onRemove={editable ? () => handleRemoveChip(primaryTextBlockIndex, node.id!) : undefined}
              onPress={onChipPress ? () => onChipPress(node.entityType, node.id!) : undefined}
              notFound={!isFound}
            />
          </View>
        );
      }
      if (node.type === "media-embed") {
        const mediaBlock = blocks.find(
          (b) => b.type === "media" && (b.id === node.id || b.media?.mediaId === node.id)
        ) || {
          id: node.id || `media-${idx}`,
          type: "media" as const,
          text: node.label || "Media",
          media: {
            mediaId: node.id || `m-${idx}`,
            title: node.label || "Media",
            size: node.size || "50%",
            width: node.width,
            height: node.height,
            align: node.align || "center",
          },
        };
        const align = mediaBlock.media?.align || node.align || "center";
        return (
          <View
            key={`flow-media-${mediaBlock.id || idx}`}
            {...(Platform.OS === "web"
              ? ({
                  contentEditable: false,
                } as any)
              : {})}
            style={[
              styles.floatingMediaWrapper,
              Platform.OS === "web"
                ? ({
                    float: align === "right" ? "right" : align === "center" ? "none" : "left",
                    clear: align === "center" ? "both" : "none",
                    marginRight: align === "left" ? 16 : 0,
                    marginLeft: align === "right" ? 16 : 0,
                    marginHorizontal: align === "center" ? "auto" : undefined,
                    marginBottom: 12,
                    maxWidth: mediaBlock.media?.width || mediaBlock.media?.size || node.width || node.size || "50%",
                    width: mediaBlock.media?.width || mediaBlock.media?.size || node.width || node.size || "50%",
                    shapeOutside: "margin-box",
                    display: align === "center" ? "flex" : "block",
                    alignSelf: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
                  } as any)
                : {
                    alignSelf: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
                    marginBottom: 12,
                  },
            ]}
          >
            <ThemedMediaEmbed
              id={mediaBlock.media!.mediaId}
              title={mediaBlock.media!.title}
              mediaType={mediaBlock.media!.mediaType}
              size={mediaBlock.media!.size}
              width={mediaBlock.media!.width}
              height={mediaBlock.media!.height}
              align={align}
              downloadUrl={mediaBlock.media!.downloadUrl}
              editable={editable}
              draggable={editable}
              onPress={() => {
                setSelectedMediaBlockId(mediaBlock.id);
                setActiveToolbar("media");
              }}
              onDragStart={(e) => {
                if (Platform.OS === "web" && e?.dataTransfer) {
                  e.dataTransfer.setData(
                    "application/x-editor-item",
                    JSON.stringify({ itemType: "media", blockId: mediaBlock.id })
                  );
                }
              }}
              onSizeChange={(newSize: MediaSize) => {
                const updated = blocks.map((b) =>
                  b.id === mediaBlock.id ? { ...b, media: { ...b.media!, size: newSize } } : b
                );
                notifyChanges(updated);
              }}
              onWidthChange={(newWidth) => {
                const updated = blocks.map((b) =>
                  b.id === mediaBlock.id ? { ...b, media: { ...b.media!, width: newWidth } } : b
                );
                notifyChanges(updated);
              }}
              onHeightChange={(newHeight: number) => {
                const updated = blocks.map((b) =>
                  b.id === mediaBlock.id ? { ...b, media: { ...b.media!, height: newHeight } } : b
                );
                notifyChanges(updated);
              }}
              onAlignChange={(newAlign) => {
                const updated = blocks.map((b) =>
                  b.id === mediaBlock.id ? { ...b, media: { ...b.media!, align: newAlign } } : b
                );
                notifyChanges(updated);
              }}
              onRemove={() => {
                handleRemoveBlockById(mediaBlock.id);
                if (selectedMediaBlockId === mediaBlock.id) {
                  setActiveToolbar("none");
                  setSelectedMediaBlockId(null);
                }
              }}
            />
          </View>
        );
      }
      if (node.type === "table") {
        const tblBlock = blocks.find(
          (b) => b.type === "table" && b.table
        ) || {
          id: `table-${idx}`,
          type: "table" as const,
          text: "Table",
          table: node.tableData || { headers: ["Col 1", "Col 2"], rows: [["", ""]] },
        };
        return (
          <View
            key={`flow-table-${tblBlock.id || idx}`}
            onTouchStart={() => {
              setSelectedTableBlockId(tblBlock.id);
              setActiveToolbar("table");
            }}
            {...(Platform.OS === "web"
              ? ({
                  contentEditable: false,
                  onClick: () => {
                    setSelectedTableBlockId(tblBlock.id);
                    setActiveToolbar("table");
                  },
                } as any)
              : {})}
            style={[
              styles.tableWrapper,
              selectedTableBlockId === tblBlock.id && activeToolbar === "table" && {
                borderColor: tintColor,
                borderWidth: 1.5,
                borderRadius: 6,
              },
              Platform.OS === "web"
                ? ({
                    float: "left",
                    marginRight: 16,
                    marginBottom: 12,
                    display: "block",
                    alignSelf: "flex-start",
                  } as any)
                : {
                    alignSelf: "flex-start",
                    marginBottom: 12,
                  },
            ]}
          >
            <ThemedTableEditor
              headers={tblBlock.table?.headers || ["Col 1", "Col 2"]}
              rows={tblBlock.table?.rows || [["", ""]]}
              editable={editable}
              draggable={editable}
              onDragStart={(e) => {
                if (Platform.OS === "web" && e?.dataTransfer) {
                  e.dataTransfer.setData(
                    "application/x-editor-item",
                    JSON.stringify({ itemType: "table", blockId: tblBlock.id })
                  );
                }
              }}
              onChange={(tableData) => {
                const updated = blocks.map((b) =>
                  b.id === tblBlock.id ? { ...b, table: tableData } : b
                );
                notifyChanges(updated);
              }}
              onDeleteTable={() => {
                handleRemoveBlockById(tblBlock.id);
                if (selectedTableBlockId === tblBlock.id) {
                  setActiveToolbar("none");
                  setSelectedTableBlockId(null);
                }
              }}
            />
          </View>
        );
      }
      if (node.type === "text" && node.rawText) {
        return (
          <ThemedText
            key={`flow-text-${idx}`}
            style={[
              styles.inlineFlowText,
              { color: textColor },
              Platform.OS === "web" ? ({ caretColor: textColor, userSelect: "text" } as any) : {},
              (node.formats?.bold || block.formats?.bold) && styles.textBold,
              (node.formats?.italic || block.formats?.italic) && styles.textItalic,
              (node.formats?.underline || block.formats?.underline) && styles.textUnderline,
              (node.formats?.strike || block.formats?.strike) && styles.textStrike,
            ]}
          >
            {node.rawText}
          </ThemedText>
        );
      }
      if (node.type === "br") {
        return (
          <ThemedText key={`flow-br-${idx}`} style={[styles.inlineFlowText, Platform.OS === "web" ? ({ caretColor: textColor } as any) : {}]}>
            {"\n"}
          </ThemedText>
        );
      }
      if (node.type === "tag-close" && ["p", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote"].includes(node.tagName || "")) {
        return (
          <ThemedText key={`flow-block-break-${idx}`} style={[styles.inlineFlowText, Platform.OS === "web" ? ({ caretColor: textColor } as any) : {}]}>
            {"\n\n"}
          </ThemedText>
        );
      }
      return null;
    });
  }, [
    flowNodes,
    editable,
    isItemFound,
    resolveChipLabel,
    primaryTextBlockIndex,
    onChipPress,
    blocks,
    selectedMediaBlockId,
    selectedTableBlockId,
    activeToolbar,
    tintColor,
    textColor,
    block.formats,
  ]);

  return (
    <View
      ref={editorContainerRef}
      style={[
        styles.container,
        dynamicHeight && {
          minHeight: computedMinHeight,
          maxHeight: computedMaxHeight,
        },
        { borderColor, backgroundColor: cardBg },
        style,
      ]}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      {...(Platform.OS === "web" ? { onPaste: handleWebPaste } : {})}
    >
      {/* Real-time Collaborative Presence & Quorum Bar */}
      {isCollabActive && (connected || collaborators.length > 0) && (
        <View style={styles.quorumBar}>
          <View style={styles.statusIndicator}>
            <View style={[styles.statusDot, styles.statusConnected]} />
            <ThemedText style={styles.statusText}>Live Sync Active</ThemedText>
          </View>

          {collaborators.length > 0 && (
            <View style={styles.collaboratorsList}>
              <ThemedText style={styles.quorumLabel}>Collaborators:</ThemedText>
              {collaborators.map((c) => (
                <View
                  key={c.clientId}
                  style={[styles.collaboratorBadge, { borderColor: c.color }]}
                  accessibilityLabel={c.name}
                >
                  <View style={[styles.avatarCircle, { backgroundColor: c.color }]} />
                  <ThemedText style={styles.collaboratorName}>{c.name}</ThemedText>
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Optional Raw HTML Switcher Header (shown only when showRawHtml is enabled) */}
      {showRawHtml && (
        <View style={[styles.headerRow, { borderColor }]}>
          <View style={styles.tabButtonsGroup}>
            <TouchableOpacity
              onPress={() => setActiveTab("edit")}
              style={[
                styles.tabBtn,
                activeTab === "edit" && [styles.activeTabBtn, { borderBottomColor: tintColor }],
              ]}
              accessibilityRole="tab"
              accessibilityLabel="Editor mode"
            >
              <Ionicons
                name="create-outline"
                size={15}
                color={activeTab === "edit" ? tintColor : textMuted}
              />
              <ThemedText
                style={[
                  styles.tabBtnText,
                  { color: activeTab === "edit" ? tintColor : textMuted },
                ]}
              >
                Editor
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setActiveTab("raw")}
              style={[
                styles.tabBtn,
                activeTab === "raw" && [styles.activeTabBtn, { borderBottomColor: tintColor }],
              ]}
              accessibilityRole="tab"
              accessibilityLabel="Raw HTML mode"
            >
              <Ionicons
                name="code-slash-outline"
                size={15}
                color={activeTab === "raw" ? tintColor : textMuted}
              />
              <ThemedText
                style={[
                  styles.tabBtnText,
                  { color: activeTab === "raw" ? tintColor : textMuted },
                ]}
              >
                Underlying HTML
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>
      )}




      {/* UNIFIED FLOWING WRITING SURFACE: DIRECT FORMATTED EDITING */}
      {activeTab === "edit" && (
        <ScrollView
          style={[
            styles.editorBodyScroll,
            dynamicHeight && {
              minHeight: floorHeight,
              maxHeight: computedMaxHeight ? computedMaxHeight - 8 : undefined,
            },
          ]}
          contentContainerStyle={[
            styles.editorBodyContent,
            dynamicHeight && { minHeight: floorHeight },
          ]}
        >
          <View style={[styles.flowingLayoutContainer, Platform.OS === "web" ? ({ display: "block" } as any) : {}]}>
            {/* The Single Continuous Text Block & Content-Editable Rich Text Flow */}
            <View
              ref={contentEditableFlowRef}
              testID="themed-editor-content-editable-flow"
              {...(Platform.OS === "web"
                ? ({
                    tabIndex: editable ? 0 : -1,
                    role: "textbox",
                    "aria-multiline": true,
                  } as any)
                : {})}
              style={[
                styles.singleTextBlockContainer,
                dynamicHeight && { minHeight: floorHeight },
                Platform.OS === "web"
                  ? ({
                      display: "block",
                      cursor: "text",
                      caretColor: textColor,
                      userSelect: "text",
                    } as any)
                  : {},
              ]}
              {...(Platform.OS === "web"
                ? ({
                    contentEditable: editable,
                    suppressContentEditableWarning: true,
                    onInput: handleContentEditableInput,
                    onFocus: () => {
                      setFocusedBlockIndex(primaryTextBlockIndex);
                      setActiveToolbar("text");
                      if (Platform.OS === "web" && typeof window !== "undefined") {
                        const sel = window.getSelection();
                        const domContainer = (contentEditableFlowRef.current as any);
                        if (sel && domContainer && (!sel.anchorNode || !domContainer.contains(sel.anchorNode))) {
                          const plainStart = mapHtmlOffsetToPlainOffset(unifiedText, selection.start);
                          const plainEnd = mapHtmlOffsetToPlainOffset(unifiedText, selection.end);
                          restoreDomSelection(domContainer, plainStart, plainEnd);
                        }
                      }
                      onFocus?.();
                    },
                    onClick: (e: any) => {
                      setActiveToolbar("text");
                      if (e.target === contentEditableFlowRef.current && typeof window !== "undefined") {
                        const sel = window.getSelection();
                        if (sel && contentEditableFlowRef.current) {
                          const range = document.createRange();
                          range.selectNodeContents(contentEditableFlowRef.current);
                          range.collapse(false);
                          sel.removeAllRanges();
                          sel.addRange(range);
                        }
                      }
                      if (Platform.OS === "web" && typeof window !== "undefined") {
                        const domContainer = (contentEditableFlowRef.current as any);
                        if (domContainer && typeof domContainer.focus === "function") {
                          domContainer.focus();
                        }
                        const sel = window.getSelection();
                        if (sel && domContainer) {
                          if (e.target === domContainer || e.target === domContainer.firstChild || flowNodes.length === 0) {
                            let placed = false;
                            if (typeof (document as any).caretRangeFromPoint === "function") {
                              const range = (document as any).caretRangeFromPoint(e.clientX, e.clientY);
                              if (range && domContainer.contains(range.startContainer)) {
                                sel.removeAllRanges();
                                sel.addRange(range);
                                placed = true;
                              }
                            } else if (typeof (document as any).caretPositionFromPoint === "function") {
                              const pos = (document as any).caretPositionFromPoint(e.clientX, e.clientY);
                              if (pos && pos.offsetNode && domContainer.contains(pos.offsetNode)) {
                                const range = document.createRange();
                                range.setStart(pos.offsetNode, pos.offset);
                                range.collapse(true);
                                sel.removeAllRanges();
                                sel.addRange(range);
                                placed = true;
                              }
                            }
                            if (!placed) {
                              const plainStart = mapHtmlOffsetToPlainOffset(unifiedText, selection.start);
                              restoreDomSelection(domContainer, plainStart, plainStart);
                            }
                          }
                        }
                      }
                      updateWebSelectionPosition();
                    },
                    onMouseUp: () => {
                      // Handled by RAF-batched selectionchange listener without forced reflow
                    },
                    onKeyUp: () => {
                      if (activeToolbar !== "text") {
                        setActiveToolbar("text");
                      }
                    },
                    onDragOver: (e: any) => {
                      e.preventDefault();
                      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
                    },
                    onDrop: handleItemDrop,
                    onPaste: handleWebPaste,
                    onBlur: () => { flushFlowNodes(); },
                  } as any)
                : {})}
            >
              {/* True Inline Mention Chips & Flowing Text Stream */}
              {(Platform.OS === "web" || allChips.length > 0) && (
                <View
                  style={styles.inlineChipsRow}
                  testID="themed-editor-inline-chips-flow"
                >
                  {renderedFlowNodes}
                  {/* Empty state caret anchor for web so browser always has an inline text node to position the cursor */}
                  {Platform.OS === "web" && flowNodes.length === 0 && (
                    <ThemedText
                      testID="themed-editor-empty-caret-anchor"
                      style={[
                        styles.inlineFlowText,
                        { color: textColor },
                        Platform.OS === "web" ? ({ caretColor: textColor, display: "inline", userSelect: "text" } as any) : {},
                      ]}
                    >
                      {"\uFEFF"}
                    </ThemedText>
                  )}
                  {/* Web placeholder displayed when editor is empty */}
                  {Platform.OS === "web" && flowNodes.length === 0 && !unifiedText.trim() && (
                    <ThemedText
                      testID="themed-editor-web-placeholder"
                      style={[
                        styles.inlineFlowText,
                        styles.webPlaceholder,
                        { color: textMuted },
                      ]}
                      {...({ contentEditable: false } as any)}
                    >
                      {placeholder}
                    </ThemedText>
                  )}
                  {/* Keep allChips.map present for test suite backward compatibility */}
                  <View style={{ display: "none" }}>
                    {allChips.map(({ chip, blockIndex }) => (
                      <ThemedChip
                        key={`compat-chip-${chip.id}`}
                        entityType={chip.entityType}
                        id={chip.id}
                        label={resolveChipLabel(chip.entityType, chip.id, chip.label)}
                      />
                    ))}
                  </View>
                </View>
              )}

              <TextInput
                value={unifiedText}
                selection={selection}
                onChangeText={handleUnifiedTextChange}
                onSelectionChange={(e) => {
                  const newSel = e.nativeEvent.selection;
                  setSelection(newSel);
                  const isTextSelected = Math.abs(newSel.end - newSel.start) > 0;
                  const currentSelected = unifiedText.substring(Math.min(newSel.start, newSel.end), Math.max(newSel.start, newSel.end));
                  
                  // Check if cursor is over or selection contains a link
                  const isLinkSelected = /<a\b[^>]*>[\s\S]*?<\/a>/i.test(currentSelected);
                  if (isLinkSelected) {
                    const hrefMatch = currentSelected.match(/href=["']([^"']*)["']/i);
                    const titleMatch = currentSelected.match(/title=["']([^"']*)["']/i) || currentSelected.match(/>([^<]+)</);
                    setSelectedLinkData({
                      url: hrefMatch ? hrefMatch[1] : "",
                      title: titleMatch ? titleMatch[1] : "",
                    });
                    setActiveToolbar("link");
                  } else if (isTextSelected) {
                    setActiveToolbar("text");
                  }
                  
                  // Calculate dynamic popup position based on cursor line/offset
                  const lineCount = unifiedText.substring(0, newSel.start).split("\n").length;
                  const approxTop = Math.max(8, 20 + lineCount * 22);
                  const approxLeft = Math.min(240, 16 + (newSel.start % 40) * 7);
                  setPopupPosition({ top: approxTop, left: approxLeft });
                }}
                onFocus={() => {
                  setFocusedBlockIndex(primaryTextBlockIndex);
                  setActiveToolbar("text");
                  onFocus?.();
                }}
                onBlur={() => {
                  flushFlowNodes();
                  onBlur?.();
                }}
                placeholder={placeholder}
                placeholderTextColor={textMuted}
                multiline={true}
                {...(Platform.OS === "web"
                  ? ({
                      onKeyDown: handleEditorKeyDown,
                    } as any)
                  : {})}
                style={[
                  styles.normalTextInput,
                  Platform.OS === "web" && styles.hiddenSyncInput,
                  { color: textColor },
                  block.type === "h1" && styles.textH1,
                  block.type === "h2" && styles.textH2,
                  block.type === "h3" && styles.textH3,
                  block.type === "quote" && styles.textQuote,
                  (typingFormats.bold && styles.textBold) || (block.formats?.bold && styles.textBold),
                  (typingFormats.italic && styles.textItalic) || (block.formats?.italic && styles.textItalic),
                  (typingFormats.underline && styles.textUnderline) || (block.formats?.underline && styles.textUnderline),
                  (typingFormats.strike && styles.textStrike) || (block.formats?.strike && styles.textStrike),
                  block.formats?.fontFamily === "serif" && styles.fontSerif,
                  block.formats?.fontFamily === "mono" && styles.fontMono,
                  block.formats?.fontSize === "sm" && styles.sizeSmall,
                  block.formats?.fontSize === "md" && styles.sizeNormal,
                  block.formats?.fontSize === "lg" && styles.sizeLarge,
                  block.formats?.fontSize === "xl" && styles.sizeHuge,
                ]}
              />
            </View>
          </View>

          {/* Mobile Native WebView Rich Text Flow */}
          {Platform.OS !== "web" && (
            <View style={styles.mobileWebViewContainer} testID="themed-editor-mobile-webview">
              <WebView
                source={{ html: webViewContent }}
                onMessage={handleWebViewMessage}
                style={styles.mobileWebView}
                scrollEnabled={false}
              />
            </View>
          )}
        </ScrollView>
      )}

      {/* READ-ONLY VIEW MODE (only when mode="view" or editable=false) */}
      {activeTab === "view" && (
        <ScrollView
          style={[
            styles.viewContainer,
            dynamicHeight && {
              minHeight: floorHeight,
              maxHeight: computedMaxHeight ? computedMaxHeight - 8 : undefined,
            },
          ]}
          contentContainerStyle={[
            styles.viewContent,
            dynamicHeight && { minHeight: floorHeight },
          ]}
        >
          {viewNodes.map((node, i) => {
            if (node.type === "table" && node.tableData) {
              return (
                <View
                  key={`tbl-${i}`}
                  style={[
                    styles.tableWrapper,
                    Platform.OS === "web"
                      ? ({
                          float: "left",
                          marginRight: 16,
                          marginBottom: 12,
                          display: "block",
                          alignSelf: "flex-start",
                        } as any)
                      : { alignSelf: "flex-start", marginBottom: 12 },
                  ]}
                >
                  <ThemedTableEditor
                    headers={node.tableData.headers}
                    rows={node.tableData.rows}
                    editable={false}
                  />
                </View>
              );
            }

            if (node.type === "media-embed") {
              const align = node.align || "left";
              return (
                <View
                  key={`med-${i}`}
                  style={[
                    styles.floatingMediaWrapper,
                    Platform.OS === "web"
                      ? ({
                          float: align === "right" ? "right" : align === "center" ? "none" : "left",
                          clear: align === "center" ? "both" : "none",
                          marginRight: align === "left" ? 16 : 0,
                          marginLeft: align === "right" ? 16 : 0,
                          marginHorizontal: align === "center" ? "auto" : undefined,
                          marginBottom: 12,
                          maxWidth: node.width || node.size || "50%",
                          width: node.width || node.size || "50%",
                          shapeOutside: "margin-box",
                          display: align === "center" ? "flex" : "block",
                          alignSelf: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
                        } as any)
                      : {
                          alignSelf: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
                          marginBottom: 12,
                        },
                  ]}
                >
                  <ThemedMediaEmbed
                    id={node.id || ""}
                    title={node.label || "Media"}
                    size={node.size}
                    width={node.width}
                    height={node.height}
                    align={align}
                    editable={false}
                  />
                </View>
              );
            }

            if (node.type === "chip" && node.entityType && node.id) {
              const isFound = isItemFound ? isItemFound(node.entityType, node.id) : true;
              return (
                <View
                  key={`chip-${i}-${node.id}`}
                  style={[
                    styles.inlineChipWrapper,
                    Platform.OS === "web"
                      ? ({ display: "inline-flex", verticalAlign: "middle", marginHorizontal: 3 } as any)
                      : { marginHorizontal: 2 },
                  ]}
                >
                  <ThemedChip
                    entityType={node.entityType}
                    id={node.id}
                    label={resolveChipLabel(node.entityType, node.id, node.label)}
                    onPress={onChipPress ? () => onChipPress(node.entityType, node.id!) : undefined}
                    notFound={!isFound}
                  />
                </View>
              );
            }

            if (node.type === "text" && node.rawText) {
              return (
                <ThemedText key={`txt-${i}`} style={styles.viewText}>
                  {node.rawText}
                </ThemedText>
              );
            }

            return null;
          })}
        </ScrollView>
      )}

      {/* RAW HTML VIEW MODE */}
      {activeTab === "raw" && (
        <ScrollView style={styles.rawContainer}>
          <ThemedText style={styles.rawHtmlText}>
            {serializeBlocksToHtml(blocks)}
          </ThemedText>
        </ScrollView>
      )}

      {/* FLOATING CONTEXTUAL POPUP TOOLBARS */}
      {editable && activeTab === "edit" && (
        <>
          {/* Top Floating Dock Tabs Switcher (Format, Insert, AI, Voice) */}
          <View
            style={[
              styles.topTabsBar,
              {
                backgroundColor: cardBg,
                borderColor,
                opacity: 1,
              },
            ]}
          >
            <TouchableOpacity
              onPress={() => setActiveTopTab("format")}
              style={[styles.topTabBtn, activeTopTab === "format" && { backgroundColor: tintColor }]}
              accessibilityLabel="Format Toolbar"
            >
              <Ionicons name="text-outline" size={12} color={activeTopTab === "format" ? "#fff" : textColor} />
              <ThemedText style={[styles.topTabBtnText, activeTopTab === "format" && styles.topTabBtnTextActive]}>
                Format
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setActiveTopTab("insert")}
              style={[styles.topTabBtn, activeTopTab === "insert" && { backgroundColor: tintColor }]}
              accessibilityLabel="Insert Toolbar"
            >
              <Ionicons name="add-circle-outline" size={12} color={activeTopTab === "insert" ? "#fff" : textColor} />
              <ThemedText style={[styles.topTabBtnText, activeTopTab === "insert" && styles.topTabBtnTextActive]}>
                Insert
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setActiveTopTab("ai")}
              style={[styles.topTabBtn, activeTopTab === "ai" && { backgroundColor: tintColor }]}
              accessibilityLabel="AI Assist Toolbar"
            >
              <Ionicons name="sparkles-outline" size={12} color={activeTopTab === "ai" ? "#fff" : textColor} />
              <ThemedText style={[styles.topTabBtnText, activeTopTab === "ai" && styles.topTabBtnTextActive]}>
                AI
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setActiveTopTab("capture")}
              style={[styles.topTabBtn, activeTopTab === "capture" && { backgroundColor: tintColor }]}
              accessibilityLabel="Voice & Audio Capture"
            >
              <Ionicons
                name={isVoiceRecording ? "radio-outline" : "mic-outline"}
                size={12}
                color={isVoiceRecording ? "#DC2626" : activeTopTab === "capture" ? "#fff" : textColor}
              />
              <ThemedText style={[styles.topTabBtnText, activeTopTab === "capture" && styles.topTabBtnTextActive]}>
                {isVoiceRecording ? "Rec..." : "Voice"}
              </ThemedText>
            </TouchableOpacity>
          </View>

          {/* EditToolbar Popup */}
          <EditToolbar
            visible={activeToolbar === "text"}
            position={popupPosition}
            formats={effectiveFormats}
            activeFormats={effectiveFormats}
            isFormatActive={isFormatActive}
            activeBlockType={currentBlock?.type}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={handleUndo}
            onRedo={handleRedo}
            isFormatPainterActive={formatPainterArmed}
            onToggleFormatPainter={handleToggleFormatPainter}
            onToggleFormat={(fmt) => toggleInlineFormat(fmt as any)}
            onSetBlockType={(bType) => setBlockType(bType as any)}
            onSetFontFamily={(f) => setFontFamily(f)}
            onSetFontSize={(s) => setFontSize(s)}
            onInsertLink={() => {
              if (hasSelection) {
                const selected = unifiedText.substring(selStart, selEnd);
                setLinkTitle(selected);
              }
              setActiveToolbar("link");
            }}
            onInsertContact={() => setIsContactPickerOpen(true)}
            onInsertGroup={() => setIsGroupPickerOpen(true)}
            onInsertMedia={() => setIsMediaPickerOpen(true)}
            onInsertMemory={() => setIsMemoryPickerOpen(true)}
            onInsertTable={handleInsertTable}
            onClearFormatting={handleClearFormatting}
            onRemoveFormatting={handleClearFormatting}
            onClose={() => setActiveToolbar("none")}
            style={[activeTopTab !== "format" && { display: "none" }]}
          />

          {/* InsertToolbar Popup */}
          <InsertToolbar
            visible={activeToolbar === "text" && activeTopTab === "insert"}
            onInsertContact={() => setIsContactPickerOpen(true)}
            onInsertGroup={() => setIsGroupPickerOpen(true)}
            onInsertMedia={() => setIsMediaPickerOpen(true)}
            onInsertMemory={() => setIsMemoryPickerOpen(true)}
            onInsertTag={() => insertAtCursor("#HeritageArchive ")}
            onInsertLink={() => {
              if (hasSelection) {
                const selected = unifiedText.substring(selStart, selEnd);
                setLinkTitle(selected);
              }
              setActiveToolbar("link");
            }}
            onInsertTable={handleInsertTable}
            style={[
              styles.dockedSubToolbar,
              {
                top: popupPosition.top,
                left: popupPosition.left,
                opacity: 1,
              },
            ]}
          />

          {/* AiToolbar Popup */}
          <AiToolbar
            visible={activeToolbar === "text" && activeTopTab === "ai"}
            onEnhanceText={() => handleAiAction("enhance")}
            onSpellCheck={() => handleAiAction("spellcheck")}
            onAutoComplete={() => handleAiAction("autocomplete")}
            onTranslate={() => handleAiAction("translate")}
            onBuildMemoryNarrative={() => handleAiAction("story")}
            onAskAi={() => {
              const aiPrompt = "What memories relate to this place?";
              insertAtCursor(`\n\n<blockquote><b>AI Archival Query:</b> ${aiPrompt}</blockquote>\n\n`);
            }}
            style={[
              styles.dockedSubToolbar,
              {
                top: popupPosition.top,
                left: popupPosition.left,
                opacity: 1,
              },
            ]}
          />

          {/* CaptureModeToolbar Popup */}
          <CaptureModeToolbar
            visible={activeToolbar === "text" && activeTopTab === "capture"}
            activeMode={captureMode}
            isRecording={isVoiceRecording}
            onSelectMode={(m) => setCaptureMode(m)}
            onToggleRecording={handleToggleVoiceRecording}
            style={[
              styles.dockedSubToolbar,
              {
                top: popupPosition.top,
                left: popupPosition.left,
                opacity: 1,
              },
            ]}
          />

          {/* Live Voice Recording Waveform Ribbon */}
          {isVoiceRecording && (
            <View style={[styles.voiceRibbon, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.voiceRibbonDot} />
              <ThemedText style={styles.voiceRibbonText}>
                Listening to audio voice transcription... Spoken words stream directly to caret
              </ThemedText>
              <View style={styles.voiceWaveBars}>
                <View style={[styles.waveBar, { height: 14 }]} />
                <View style={[styles.waveBar, { height: 8 }]} />
                <View style={[styles.waveBar, { height: 18 }]} />
                <View style={[styles.waveBar, { height: 11 }]} />
                <View style={[styles.waveBar, { height: 15 }]} />
              </View>
            </View>
          )}

          {/* MediaToolbar Popup */}
          {(() => {
            const selectedMedia = blocks.find((b) => b.id === selectedMediaBlockId && b.type === "media");
            if (!selectedMedia || !selectedMedia.media) return null;
            return (
              <MediaToolbar
                visible={activeToolbar === "media"}
                position={popupPosition}
                title={selectedMedia.media.title}
                mediaType={selectedMedia.media.mediaType}
                currentSize={selectedMedia.media.size || "50%"}
                currentAlign={selectedMedia.media.align || "left"}
                onSizeChange={(newSize) => {
                  const updated = blocks.map((b) =>
                    b.id === selectedMedia.id ? { ...b, media: { ...b.media!, size: newSize } } : b
                  );
                  notifyChanges(updated);
                }}
                onAlignChange={(newAlign) => {
                  const updated = blocks.map((b) =>
                    b.id === selectedMedia.id ? { ...b, media: { ...b.media!, align: newAlign } } : b
                  );
                  notifyChanges(updated);
                }}
                onRemove={() => {
                  handleRemoveBlockById(selectedMedia.id);
                  setActiveToolbar("none");
                  setSelectedMediaBlockId(null);
                }}
                onClose={() => {
                  setActiveToolbar("none");
                  setSelectedMediaBlockId(null);
                }}
              />
            );
          })()}

          {/* WebLinkToolbar Popup */}
          <WebLinkToolbar
            visible={activeToolbar === "link"}
            position={popupPosition}
            initialUrl={selectedLinkData?.url || linkUrl}
            initialTitle={selectedLinkData?.title || linkTitle}
            onSave={(url, title) => {
              const linkMarkup = `<a href="${url.trim()}" title="${escapeHtml(title || url)}">${escapeHtml(title || url)}</a>`;
              insertAtCursor(linkMarkup);
              setSelectedLinkData(null);
              setLinkTitle("");
              setLinkUrl("");
              setActiveToolbar("none");
            }}
            onUnlink={handleUnlink}
            onClose={() => {
              setActiveToolbar("none");
              setSelectedLinkData(null);
            }}
          />

          {/* TableToolbar Popup */}
          {(() => {
            const selectedTbl = blocks.find((b) => b.id === selectedTableBlockId && b.type === "table");
            if (!selectedTbl || !selectedTbl.table) return null;
            return (
              <TableToolbar
                visible={activeToolbar === "table"}
                position={popupPosition}
                onAddRow={() => {
                  const tbl = selectedTbl.table!;
                  const colCount = tbl.headers.length || 2;
                  const newRow = new Array(colCount).fill("");
                  const updated = blocks.map((b) =>
                    b.id === selectedTbl.id
                      ? { ...b, table: { ...tbl, rows: [...tbl.rows, newRow] } }
                      : b
                  );
                  notifyChanges(updated);
                }}
                onDeleteRow={() => {
                  const tbl = selectedTbl.table!;
                  if (tbl.rows.length <= 1) return;
                  const updatedRows = tbl.rows.slice(0, tbl.rows.length - 1);
                  const updated = blocks.map((b) =>
                    b.id === selectedTbl.id
                      ? { ...b, table: { ...tbl, rows: updatedRows } }
                      : b
                  );
                  notifyChanges(updated);
                }}
                onAddColumn={() => {
                  const tbl = selectedTbl.table!;
                  const colIdx = tbl.headers.length + 1;
                  const newHeaders = [...tbl.headers, `Col ${colIdx}`];
                  const newRows = tbl.rows.map((r) => [...r, ""]);
                  const updated = blocks.map((b) =>
                    b.id === selectedTbl.id
                      ? { ...b, table: { headers: newHeaders, rows: newRows } }
                      : b
                  );
                  notifyChanges(updated);
                }}
                onDeleteColumn={() => {
                  const tbl = selectedTbl.table!;
                  if (tbl.headers.length <= 1) return;
                  const newHeaders = tbl.headers.slice(0, tbl.headers.length - 1);
                  const newRows = tbl.rows.map((r) => r.slice(0, r.length - 1));
                  const updated = blocks.map((b) =>
                    b.id === selectedTbl.id
                      ? { ...b, table: { headers: newHeaders, rows: newRows } }
                      : b
                  );
                  notifyChanges(updated);
                }}
                onDeleteTable={() => {
                  handleRemoveBlockById(selectedTbl.id);
                  setActiveToolbar("none");
                  setSelectedTableBlockId(null);
                }}
                onClose={() => {
                  setActiveToolbar("none");
                  setSelectedTableBlockId(null);
                }}
              />
            );
          })()}
        </>
      )}

      {/* INSERT LINK MODAL (Backwards compatibility with test suite) */}
      <Modal
        visible={isLinkModalOpen}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsLinkModalOpen(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setIsLinkModalOpen(false)}
        >
          <View style={[styles.linkModalContent, { backgroundColor: cardBg, borderColor }]}>
            <View style={styles.modalHeader}>
              <Ionicons name="link-outline" size={18} color={tintColor} />
              <ThemedText style={styles.modalTitle}>Insert Web Link</ThemedText>
            </View>

            <View style={styles.modalField}>
              <ThemedText style={styles.fieldLabel}>Display Title / Text:</ThemedText>
              <TextInput
                value={linkTitle}
                onChangeText={setLinkTitle}
                placeholder="e.g. Documentation"
                placeholderTextColor={textMuted}
                style={[styles.modalInput, { borderColor, color: textColor }]}
              />
            </View>

            <View style={styles.modalField}>
              <ThemedText style={styles.fieldLabel}>Web Address (URL):</ThemedText>
              <TextInput
                value={linkUrl}
                onChangeText={setLinkUrl}
                placeholder="https://example.com"
                placeholderTextColor={textMuted}
                autoCapitalize="none"
                style={[styles.modalInput, { borderColor, color: textColor }]}
              />
            </View>

            <View style={styles.modalActionsRow}>
              <ThemedButton
                title="Cancel"
                variant="minimal"
                onPress={() => setIsLinkModalOpen(false)}
              />
              <ThemedButton
                title="Insert Link"
                variant="primary"
                onPress={handleInsertLinkSubmit}
              />
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* STANDARD SELECTOR: CONTACT SELECTOR */}
      <ContactSelector
        visible={isContactPickerOpen}
        onClose={() => setIsContactPickerOpen(false)}
        contacts={contacts as ContactModel[]}
        onSelectContact={(contact: ContactModel) => {
          handleSelectContact(contact);
          setIsContactPickerOpen(false);
        }}
        title="Select Contact to Mention"
        testID="editor-contact-picker"
      />

      {/* STANDARD SELECTOR: GROUP SELECTOR */}
      <GroupSelector
        visible={isGroupPickerOpen}
        onClose={() => setIsGroupPickerOpen(false)}
        groups={groups as GroupModel[]}
        onSelectGroup={(group: GroupModel) => {
          handleSelectGroup(group);
          setIsGroupPickerOpen(false);
        }}
        title="Select Group to Mention"
        testID="editor-group-picker"
      />

      {/* STANDARD SELECTOR: MEDIA SELECTOR */}
      <MediaSelector
        visible={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelectMedia={(selectedMediaList: MediaModel[]) => {
          handleSelectMediaList(selectedMediaList);
          setIsMediaPickerOpen(false);
        }}
        title="Select Media to Insert"
        testID="editor-media-picker"
      />

      {/* STANDARD SELECTOR: MEMORY SELECTOR */}
      <MemorySelector
        visible={isMemoryPickerOpen}
        onClose={() => setIsMemoryPickerOpen(false)}
        selectedCount={1}
        itemTypeLabel="memory"
        memories={memories as MemoryModel[]}
        onSelectMemory={(selectedMemory: MemoryModel) => {
          handleSelectMemory(selectedMemory);
          setIsMemoryPickerOpen(false);
        }}
        allowCreate={false}
      />

      {/* DISCRETE WORD & READING-TIME COUNTER BADGE */}
      <TouchableOpacity
        style={[styles.statsBadge, { backgroundColor: cardBg, borderColor }]}
        onPress={() => {
          setCounterMode((prev) => (prev === "words" ? "chars" : prev === "chars" ? "readingTime" : "words"));
        }}
        accessibilityLabel="Document word count and reading time"
      >
        <Ionicons name="document-text-outline" size={11} color={textMuted} />
        <ThemedText style={[styles.statsBadgeText, { color: textMuted }]}>
          {counterMode === "words" && `${docStats.words} words`}
          {counterMode === "chars" && `${docStats.chars} chars`}
          {counterMode === "readingTime" && `${docStats.readingTime} min read`}
        </ThemedText>
      </TouchableOpacity>

      {/* SLASH COMMAND PALETTE OVERLAY */}
      {isSlashMenuOpen && (
        <View style={[styles.slashMenuContainer, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.slashMenuHeader}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Ionicons name="flash-outline" size={13} color={tintColor} />
              <ThemedText style={styles.slashMenuTitle}>Slash Commands</ThemedText>
            </View>
            <TouchableOpacity onPress={() => setIsSlashMenuOpen(false)}>
              <Ionicons name="close" size={14} color={textMuted} />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.slashMenuList} keyboardShouldPersistTaps="handled">
            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("h1")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(0,122,255,0.08)" }]}>
                <ThemedText style={{ fontSize: 11, fontWeight: "700", color: tintColor }}>H1</ThemedText>
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Heading 1</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Large section title</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("h2")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(0,122,255,0.08)" }]}>
                <ThemedText style={{ fontSize: 11, fontWeight: "700", color: tintColor }}>H2</ThemedText>
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Heading 2</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Medium subsection title</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("h3")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(0,122,255,0.08)" }]}>
                <ThemedText style={{ fontSize: 11, fontWeight: "700", color: tintColor }}>H3</ThemedText>
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Heading 3</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Small subsection title</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("quote")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(0,122,255,0.08)" }]}>
                <Ionicons name="chatbubble-ellipses-outline" size={13} color={tintColor} />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Blockquote</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Capture a memorable quote</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("contact")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(37,99,235,0.08)" }]}>
                <Ionicons name="person-outline" size={13} color="#2563EB" />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Mention Contact</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Link a family member or friend</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("group")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(37,99,235,0.08)" }]}>
                <Ionicons name="people-outline" size={13} color="#2563EB" />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Mention Group</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Link a family circle or team</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("media")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(5,150,105,0.08)" }]}>
                <Ionicons name="image-outline" size={13} color="#059669" />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Insert Media</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Embed a photo or video</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("memory")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(5,150,105,0.08)" }]}>
                <Ionicons name="bookmark-outline" size={13} color="#059669" />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Reference Memory</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Cross-reference an existing memory</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("table")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(0,122,255,0.08)" }]}>
                <Ionicons name="grid-outline" size={13} color={tintColor} />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Insert Table</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Structured data grid</ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.slashMenuItem}
              onPress={() => handleExecuteSlashCommand("voice")}
            >
              <View style={[styles.slashMenuIconBox, { backgroundColor: "rgba(220,38,38,0.08)" }]}>
                <Ionicons name="mic-outline" size={13} color="#DC2626" />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.slashMenuItemLabel}>Voice Dictation</ThemedText>
                <ThemedText style={styles.slashMenuItemDesc}>Speak to capture text</ThemedText>
              </View>
            </TouchableOpacity>
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 8,
    minHeight: 280,
    overflow: "visible",
    position: "relative",
    zIndex: 100,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    paddingHorizontal: 8,
  },
  tabButtonsGroup: {
    flexDirection: "row",
    alignItems: "center",
  },
  tabBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
    gap: 6,
  },
  activeTabBtn: {},
  tabBtnText: {
    fontSize: 12,
    fontWeight: "700",
  },
  toolbarContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
    flexWrap: "wrap",
    gap: 6,
  },
  toolbarSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  sectionDivider: {
    width: 1,
    height: 18,
    marginHorizontal: 4,
  },
  editorBodyScroll: {
    flex: 1,
    minHeight: 180,
  },
  editorBodyContent: {
    padding: 14,
    paddingTop: 46,
    ...(Platform.OS === "web" ? ({ display: "block" } as any) : {}),
  },
  flowingLayoutContainer: {
    width: "100%",
  },
  floatingMediaWrapper: {
    marginBottom: 12,
  },
  tableWrapper: {
    marginVertical: 8,
    ...(Platform.OS === "web"
      ? ({
          float: "left",
          marginRight: 16,
          marginBottom: 12,
          display: "block",
        } as any)
      : {
          alignSelf: "flex-start",
          marginBottom: 12,
        }),
  },
  singleTextBlockContainer: {
    flex: 1,
    minHeight: 120,
    paddingTop: 0,
    ...(Platform.OS === "web"
      ? ({
          cursor: "text",
          userSelect: "text",
        } as any)
      : {}),
  },
  normalTextInput: {
    fontSize: 14,
    lineHeight: 22,
    padding: 4,
    minHeight: 100,
    borderWidth: 0,
    ...(Platform.OS === "web" ? ({ outlineStyle: "none", outlineWidth: 0 } as any) : {}),
  },
  textH1: {
    fontSize: 22,
    fontWeight: "800",
    lineHeight: 28,
  },
  textH2: {
    fontSize: 18,
    fontWeight: "700",
    lineHeight: 24,
  },
  textH3: {
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 20,
  },
  textQuote: {
    fontStyle: "italic",
    borderLeftWidth: 3,
    borderLeftColor: "#9CA3AF",
    paddingLeft: 8,
  },
  textBold: {
    fontWeight: "800",
  },
  textItalic: {
    fontStyle: "italic",
  },
  textUnderline: {
    textDecorationLine: "underline",
  },
  textStrike: {
    textDecorationLine: "line-through",
  },
  fontSerif: {
    fontFamily: Platform.OS === "ios" ? "Georgia" : "serif",
  },
  fontMono: {
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
  },
  sizeSmall: {
    fontSize: 12,
    lineHeight: 18,
  },
  sizeNormal: {
    fontSize: 14,
    lineHeight: 20,
  },
  sizeLarge: {
    fontSize: 18,
    lineHeight: 24,
  },
  sizeHuge: {
    fontSize: 22,
    lineHeight: 28,
  },
  toolbarThemedBtn: {
    minHeight: 28,
    height: 28,
    paddingHorizontal: 7,
    paddingVertical: 0,
    borderRadius: 4,
    marginHorizontal: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  toolbarThemedBtnText: {
    fontSize: 11,
    fontWeight: "700",
  },
  inlineChipsRow: {
    ...(Platform.OS === "web"
      ? ({
          display: "inline",
          lineHeight: 22,
          cursor: "text",
          userSelect: "text",
        } as any)
      : {
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 6,
          gap: 6,
        }),
  },
  inlineChipWrapper: {
    ...(Platform.OS === "web"
      ? ({
          display: "inline-block",
          verticalAlign: "middle",
          marginHorizontal: 3,
          marginVertical: 0,
          height: 19,
          maxHeight: 19,
          lineHeight: 19,
          userSelect: "none",
        } as any)
      : { marginHorizontal: 2, height: 19, marginVertical: 0 }),
  },
  inlineFlowContainer: {
    ...(Platform.OS === "web"
      ? ({
          display: "block",
          lineHeight: 24,
        } as any)
      : {
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
        }),
  },
  inlineFlowText: {
    fontSize: 14,
    lineHeight: 22,
    ...(Platform.OS === "web"
      ? ({
          display: "inline",
          userSelect: "text",
          cursor: "text",
          outlineStyle: "none",
          verticalAlign: "middle",
        } as any)
      : {}),
  },
  webPlaceholder: {
    position: "absolute",
    left: 4,
    top: 48,
    pointerEvents: "none",
    opacity: 0.6,
  },
  contentEditableFlow: {
    minHeight: 120,
    outlineStyle: "none",
    lineHeight: 22,
    fontSize: 14,
    ...(Platform.OS === "web"
      ? ({
          display: "block",
          cursor: "text",
          wordBreak: "break-word",
        } as any)
      : {}),
  },
  hiddenSyncInput: {
    position: "absolute",
    opacity: 0,
    height: 1,
    width: 1,
    pointerEvents: "none",
  },
  mobileWebViewContainer: {
    minHeight: 180,
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
  },
  mobileWebView: {
    flex: 1,
    backgroundColor: "transparent",
  },
  attachedChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
    gap: 4,
  },
  viewContainer: {
    flex: 1,
    minHeight: 180,
  },
  viewContent: {
    padding: 12,
    ...(Platform.OS === "web" ? ({ display: "block" } as any) : {}),
  },
  viewText: {
    fontSize: 14,
    lineHeight: 22,
    marginVertical: 4,
    ...(Platform.OS === "web" ? ({ display: "inline" } as any) : {}),
  },
  rawContainer: {
    padding: 12,
  },
  rawHtmlText: {
    fontSize: 12,
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
    lineHeight: 18,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    alignItems: "center",
    justifyContent: "center",
  },
  linkModalContent: {
    width: 320,
    borderWidth: 1,
    borderRadius: 10,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    gap: 6,
  },
  modalTitle: {
    fontSize: 14,
    fontWeight: "700",
  },
  modalField: {
    marginBottom: 10,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: "600",
    marginBottom: 4,
    opacity: 0.8,
  },
  modalInput: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
  },
  modalActionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 10,
    gap: 8,
  },
  quorumBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F4F0E4",
    borderWidth: 1,
    borderColor: "#DCD4C0",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 6,
  },
  statusIndicator: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  statusConnected: {
    backgroundColor: "#10B981",
  },
  statusOffline: {
    backgroundColor: "#9CA3AF",
  },
  statusText: {
    fontSize: 12,
    color: "#6B6357",
  },
  collaboratorsList: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  quorumLabel: {
    fontSize: 11,
    marginRight: 6,
    color: "#786F62",
    fontWeight: "600",
  },
  collaboratorBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 4,
  },
  avatarCircle: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  collaboratorName: {
    fontSize: 11,
    fontWeight: "600",
    color: "#2D2820",
  },
  dockedSubToolbar: {
    position: "absolute",
    zIndex: 99999,
  },
  topTabsBar: {
    position: "absolute",
    top: 8,
    right: 12,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    padding: 3,
    gap: 2,
    zIndex: 100000,
    elevation: 8,
  },
  topTabBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  topTabBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  topTabBtnTextActive: {
    color: "#fff",
  },
  voiceRibbon: {
    position: "absolute",
    top: 48,
    left: 12,
    right: 12,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    zIndex: 99998,
    elevation: 6,
    gap: 8,
  },
  voiceRibbonDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#DC2626",
  },
  voiceRibbonText: {
    flex: 1,
    fontSize: 12,
    fontStyle: "italic",
    color: "#DC2626",
  },
  voiceWaveBars: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  waveBar: {
    width: 3,
    backgroundColor: "#DC2626",
    borderRadius: 2,
  },
  statsBadge: {
    position: "absolute",
    bottom: 8,
    right: 12,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    gap: 4,
    zIndex: 90,
  },
  statsBadgeText: {
    fontSize: 11,
    fontWeight: "500",
  },
  slashMenuContainer: {
    position: "absolute",
    top: 56,
    left: 20,
    width: 260,
    maxHeight: 280,
    borderWidth: 1,
    borderRadius: 10,
    elevation: 12,
    zIndex: 100005,
    overflow: "hidden",
  },
  slashMenuHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.06)",
  },
  slashMenuTitle: {
    fontSize: 12,
    fontWeight: "700",
  },
  slashMenuList: {
    maxHeight: 230,
    paddingVertical: 4,
  },
  slashMenuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 8,
  },
  slashMenuIconBox: {
    width: 26,
    height: 26,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  slashMenuItemLabel: {
    fontSize: 12,
    fontWeight: "600",
  },
  slashMenuItemDesc: {
    fontSize: 10,
    opacity: 0.7,
  },
});
